import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Alert,
  AlertTitle,
  Chip,
  Button,
  Grid,
  Tabs,
  Tab
} from '@mui/material'
import { GetApp as DownloadIcon } from '@mui/icons-material'
import PsychrometricChart from '../charts/PsychrometricChart'
import StatePointsInfo from '../charts/StatePointsInfo'
import { PsychrometricCalculator } from '../../utils/psychrometric'
import { exportAHSelectionToExcel } from '../../utils/excelExport'
import { extractOutputSheetData } from '../../utils/outputSheetDataExtractor'

// OUTDOOR_CONDITIONS - 기본 외기 조건 데이터
// 절대습도와 엔탈피는 설정 시점에 계산: 건구온도 + 상대습도 → 절대습도, 건구온도 + 절대습도 → 엔탈피
const OUTDOOR_CONDITIONS = (() => {
  const calculateCondition = (temperature: number, humidity: number, elevation: number = 0) => {
    // 1단계: 건구온도 + 상대습도 + 고도로 절대습도 계산
    const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(temperature, humidity, elevation);

    // 2단계: 절대습도를 VBA 방식으로 4자리 반올림 (Application.Round(x, 4))
    const humidityRatioRounded = PsychrometricCalculator.vbaRound(humidityRatio, 4);

    // 3단계: 건구온도 + 절대습도(4자리 반올림)로 엔탈피 계산
    const enthalpy = PsychrometricCalculator.calculateEnthalpy(
      temperature,
      humidityRatioRounded
    );

    return {
      temperature,
      humidity,
      humidityRatio: humidityRatioRounded,
      enthalpy
    };
  };

  return [
    {
      code: 'A',
      name: '서울 표준',
      summer: calculateCondition(35, 55),
      winter: calculateCondition(-10, 70)
    },
    {
      code: 'B',
      name: '부산 표준',
      summer: calculateCondition(32, 65),
      winter: calculateCondition(-5, 65)
    }
  ];
})()

/**
 * psychroChartData가 없을 때 calculationResults(AHUResults)에서 냉방/난방 코일 용량 계산에
 * 필요한 상태점(ma, l1, m1, l2)만 합성하여 반환한다.
 * 저장 파일에서 psychroChartData는 용량 문제로 제외되므로, 파일을 열었을 때에도
 * 저장된 calculationResults로 계산을 이어갈 수 있게 한다.
 */
const buildPsychroChartDataFromCalc = (calcData: any): { points: any[]; lines: any[] } | null => {
  if (!calcData || typeof calcData !== 'object') return null
  const toPoint = (name: string, state: any) => {
    if (!state || typeof state !== 'object') return null
    const point: any = { name }
    if (state.dryBulbTemp !== undefined) point.dryBulbTemp = state.dryBulbTemp
    if (state.relativeHumidity !== undefined) point.relativeHumidity = state.relativeHumidity
    if (state.humidityRatio !== undefined) point.humidityRatio = state.humidityRatio
    if (state.enthalpy !== undefined) point.enthalpy = state.enthalpy
    return point
  }
  const points: any[] = []
  const ma = toPoint('ma', calcData.mixedAir)
  const l1 = toPoint('l1', calcData.supplyAir)
  const m1 = toPoint('m1', calcData.heatingMixedAir)
  const l2 = toPoint('l2', calcData.heatingSupplyAir)
  const r2 = toPoint('r2', calcData.heatingReturnAir)
  const l3 = toPoint('l3', calcData.heatingAfterHeating)
  if (ma) points.push(ma)
  if (l1) points.push(l1)
  if (m1) points.push(m1)
  if (l2) points.push(l2)
  if (r2) points.push(r2)
  if (l3) points.push(l3)
  if (points.length === 0) return null
  return { points, lines: [] }
}

/** currentAHUData에서 유효한 psychroChartData를 얻거나, 없으면 calculationResults로부터 합성 */
const resolvePsychroChartData = (currentAHUData: any): { points: any[]; lines: any[] } | null => {
  if (currentAHUData?.psychroChartData?.points?.length > 0) {
    return currentAHUData.psychroChartData
  }
  return buildPsychroChartDataFromCalc(currentAHUData?.calculationResults)
}

interface AHU {
  id: string
  ahuNumber: string
  systemType: string
  airVolume: number
  supplyTemp: number
  returnTemp: number
  coolingCapacity: number
  heatingCapacity: number
  fanAirflow: number
  fanPressure: number
  fanPower: number
  // 추가 항목들
  indoorCoolingLoad?: number      // 실내냉방부하 (W)
  indoorHeatingLoad?: number      // 실내난방부하 (W)
  outdoorCoolingLoad?: number     // 외기냉방부하 (W)
  outdoorHeatingLoad?: number     // 외기난방부하 (W)
  totalCoolingLoad?: number       // 전체냉방부하 (W)
  totalHeatingLoad?: number       // 전체난방부하 (W)
  location?: string               // 설치위치
  airVolumeRatio?: number         // 풍량비 (%)
  usage?: string                  // 사용처
  outdoorAirVolume?: number       // 외기량 (CMH)
  exhaustAirflow?: number         // 배기량 (CMH)
  pressurizationAirflow?: number  // 가압량 (CMH)
  // PsychrometricChart 관련 데이터
  formData?: any;
  calculationResults?: any;
  psychroChartData?: {
    points: any[];
    lines: any[];
  };
}

interface AHSelInlinePreviewProps {
  ahuData: AHU[]
  projectName?: string
  onExportExcel?: () => void
  currentProjectAHUs?: any[]
  ahuCalculationResults?: any
  currentAHUId?: string | null
  psychrometricData?: {
    points: any[],
    lines: any[],
    image?: string,  // 고해상도 PNG 이미지 (Base64)
    metadata?: any
  } | null
}

const AHSelInlinePreview: React.FC<AHSelInlinePreviewProps> = ({
  ahuData,
  projectName,
  onExportExcel,
  currentProjectAHUs,
  ahuCalculationResults,
  currentAHUId,
  psychrometricData
}) => {
  // 선택된 AHU 탭 상태 관리
  const [selectedAHUIndex, setSelectedAHUIndex] = useState(0);

  const handleAHUTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedAHUIndex(newValue);
  };

  // AHU 데이터 구조 및 계산 결과 확인용 로그
  const downloadAHSelection = async () => {
    try {
      // currentProjectAHUs를 사용 (실제 출력시트에 표시되는 데이터)
      const ahuDataToExport = currentProjectAHUs || ahuData;

      if (!ahuDataToExport || ahuDataToExport.length === 0) {
        alert('출력할 AHU 데이터가 없습니다.');
        return;
      }

      // 각 AHU에 대해 출력시트와 동일한 데이터 계산
      const enrichedAhuData = ahuDataToExport.map(ahu => {
        const currentAHU = currentProjectAHUs?.find(projectAhu =>
          projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
        ) || {};

        const form = currentAHU.formData || {};
        const calc = currentAHU.calculationResults || {};

        // 출력 시트와 동일한 계산 로직 적용
        // localStorage에서 최신 outdoorCoolingLoad 가져오기
        let outdoorCoolingLoadFromStorage = 0;
        try {
          const calcResults = JSON.parse(localStorage.getItem('calculationResults') || '{}');
          outdoorCoolingLoadFromStorage = calcResults[currentAHU.id] || 0;
        } catch (e) {
          // localStorage 읽기 실패
        }

        const outdoorCoolingLoad = parseFloat(form.outdoorCoolingLoad) ||
                                  outdoorCoolingLoadFromStorage ||
                                  calc.outdoorCoolingLoad ||
                                  currentAHU.outdoorCoolingLoad || 0;

        // 계산된 outdoorCoolingLoad를 포함한 enriched 데이터 생성
        const enrichedCalc = {
          ...calc,
          outdoorCoolingLoad // localStorage에서 가져온 최신 값으로 덮어쓰기
        };

        const enrichedAHU = {
          ...ahu,
          ...currentAHU,
          calculationResults: enrichedCalc,
          // 습공기선도 이미지 및 데이터 명시적으로 보존
          psychroChartImage: currentAHU.psychroChartImage || ahu.psychroChartImage,
          psychroChartData: currentAHU.psychroChartData || ahu.psychroChartData,
          outputSheetData: extractOutputSheetData(
            { ...ahu, ...currentAHU, calculationResults: enrichedCalc },
            currentProjectAHUs || [],
            projectName || '프로젝트',
            {} // currentProject는 props에 없으므로 빈 객체
          )
        };

        return enrichedAHU;
      });

      await exportAHSelectionToExcel(enrichedAhuData, projectName || '프로젝트');
    } catch (error) {
      console.error('❌ Excel 다운로드 실패:', error);
      console.error('에러 상세:', error);
      alert('엑셀 파일을 생성하는 중 오류가 발생하였습니다.');
    }
  }

  // AHU 객체에서 직접 formData, calculationResults를 사용하여 차트 데이터 생성
  // getChartData 함수 완전히 제거 (이제 각 AHU의 psychroChartData를 직접 사용)

  if (ahuData.length === 0) {
    return (
      <Box>
        <Alert severity="warning">
          <AlertTitle>표시할 데이터가 없습니다</AlertTitle>
          먼저 AHU 데이터를 입력하고 계산을 완료해주세요.
        </Alert>
      </Box>
    )
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
      {/* 헤더 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="div" fontWeight="bold" color="#1976d2">
          AH Selection (공조기 선정표)
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={downloadAHSelection}
          sx={{ fontSize: '0.875rem', px: 2 }}
        >
          Download
        </Button>
      </Box>

      {/* AHU 탭 네비게이션 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedAHUIndex}
          onChange={handleAHUTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="AHU selection tabs"
        >
          {currentProjectAHUs.map((ahu, index) => (
            <Tab
              key={ahu.id}
              label={`${ahu.ahuNo || `AHU-${index + 1}`}`}
              sx={{
                fontWeight: selectedAHUIndex === index ? 'bold' : 'normal',
                fontSize: '0.95rem'
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box>
          {/* 선택된 AHU만 표시 */}
          {currentProjectAHUs.map((ahu, index) => (
            <Box
              key={ahu.id}
              sx={{
                display: selectedAHUIndex === index ? 'block' : 'none',
                mb: 4
              }}
            >
              {/* AHU 헤더 */}
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid #ddd',
                mb: 2 
              }}>
                <Typography variant="h6" component="div" color="primary" gutterBottom>
                  {ahu.ahuNo}: {ahu.formData?.location || '설치위치'} 
                  <Chip 
                    label={`수량: 1`} 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                  <Chip 
                    label={`설치위치: ${ahu.formData?.location || '미정'}`} 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>

              {/* 실내 부하 / 부하 분석 / 설계 조건 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" component="div" sx={{
                  backgroundColor: '#000',
                  color: 'white',
                  p: 1,
                  fontWeight: 'bold'
                }}>
                  ■ 실내 부하 / 부하 분석 / 설계 조건
                </Typography>

                {/* 실데이터 기반 맞춤 테이블 (CSV/이미지 형식만 참고) */}
                {(() => {
                  // 스타일
                  const rightBg = { backgroundColor: '#fff9e5' };
                  const borderStyle = { border: '1px solid #ddd' };

                  // 데이터 추출
                  const currentAHU = currentProjectAHUs?.find(projectAhu =>
                    projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
                  ) || {};

                  const form = currentAHU.formData || {};
                  const calc = currentAHU.calculationResults || {};

                  // psychroChartData가 없으면 calculationResults에서 합성 (저장 파일 로드 시 폴백)
                  const effectivePsychroChartData = resolvePsychroChartData(currentAHU);

                  // 주요 값
                  const personnel = parseInt(form.personnel || form.occupantCount) || 0;
                  const area = parseFloat(form.conditionedArea || form.airConditioningArea || form.공조면적 || form.area) || 0;
                  const ceilingHeight = parseFloat(form.ceilingHeight) || 0;
                  const volume = area && ceilingHeight ? Math.round(area * ceilingHeight) : 0;
                  const volumeFormatted = volume > 0 ? volume.toLocaleString() : '0';
                  const personnelPerHour = parseFloat(form.airflowPerPerson || form.personnelPerHour) || 25;
                  const rshf = (() => {
                    const sensible = parseFloat(form.sensibleCoolingLoad) || 0;
                    const latent = parseFloat(form.latentCoolingLoad) || 0;
                    const total = sensible + latent;
                    return total > 0 ? (sensible / total) : 0;
                  })();
                  const systemType = form.systemType || currentAHU.systemType || 'VAV';
                  const location = form.location || '';
                  const ahuRoomCount = form.roomCount || '';
                  const zoneBlockPeakTime = form.zoneBlockPeakTime || '15:00';

                  // 부하값
                  const sensibleHeatLoad = parseFloat(form.sensibleCoolingLoad) || 0;
                  const latentHeatLoad = parseFloat(form.latentCoolingLoad) || 0;
                  const ventilationLightingLoad = parseFloat(form.ventilationLightingLoad) || 0;
                  const heatingLoad = parseFloat(form.heatingLoad) || 0;

                  // 급기량: 다양한 경로에서 급기량 값 찾기 (먼저 계산)
                  const supplyAirflow =
                    // 1순위: 풍량선정 탭 계산 결과
                    calc.calculatedAirflows?.supplyAirflow ||
                    currentAHU.calculatedAirflows?.supplyAirflow ||
                    // 2순위: 직접 계산 결과
                    calc.supplyAirflow ||
                    // 3순위: 폼 데이터의 직접입력값
                    parseFloat(form.directSupplyAirflow) ||
                    parseFloat(form.supplyAirflow) ||
                    // 4순위: AHU 기본값
                    ahu.airVolume ||
                    ahu.fanAirflow ||
                    // 5순위: (냉방 현열 부하 + 환기 조명 부하) 기반 계산 (급기온도차 10도 가정)
                    // VBA 공식: 급기량 = (냉방 현열 부하 + 환기 조명 부하) / (0.335 × △T)
                    (sensibleHeatLoad + ventilationLightingLoad > 0 ? Math.round((sensibleHeatLoad + ventilationLightingLoad) / (0.335 * 10)) : 0);

                  // 급기 풍량(m³/h/m²) = 급기량 / 공조면적
                  const supplyAirPerArea = area > 0 ? (supplyAirflow / area).toFixed(2) : '';

                  // 환기 횟수(a.c/h) = 급기량 / 체적(면적 × 천장고)
                  const airChange = volume > 0 ? (supplyAirflow / volume).toFixed(2) : '';

                  // ===== 장치부하 통합 계산 함수 (한번만 계산하여 모든 곳에서 재사용) =====
                  const calculateDeviceLoads = () => {
                    let deviceCoolingLoad = 0;
                    let deviceHeatingLoad = 0;
                    let mixedAirEnthalpy = 0;
                    let supplyAirEnthalpy = 0;
                    let enthalpyDiff = 0;
                    let mixedAirTemp = 0;
                    let supplyAirTemp = 0;
                    let tempDiff = 0;
                    let coolingCalculationError = '';
                    let heatingCalculationError = '';

                    // 냉방 계산
                    if (calc.coolingCapacity) {
                      deviceCoolingLoad = calc.coolingCapacity;
                    } else if (currentAHU.coolingCapacity) {
                      deviceCoolingLoad = currentAHU.coolingCapacity;
                    } else if (ahu.coolingCapacity) {
                      deviceCoolingLoad = ahu.coolingCapacity;
                    } else {
                      // 습공기 선도에서 엔탈피 값 계산
                      if (effectivePsychroChartData && effectivePsychroChartData.points) {
                        const psychroPoints = effectivePsychroChartData.points;

                        // 혼합공기 포인트 찾기 (정확한 이름: 'ma')
                        const mixedAirPoint = psychroPoints.find(p => p.name === 'ma');

                        // 냉각후 급기 포인트 찾기 (정확한 이름: 'l1')
                        const supplyAirPoint = psychroPoints.find(p => p.name === 'l1');


                        if (mixedAirPoint && supplyAirPoint) {
                          // 엔탈피 값이 직접 있는 경우
                          if (mixedAirPoint.enthalpy !== undefined && supplyAirPoint.enthalpy !== undefined) {
                            // ✅ VBA 원본: Application.Round(EA1ent, 2) - Application.Round(LA1ent, 2)
                            // 엔탈피를 2자리 반올림한 후 차이 계산
                            const roundedMixedEnthalpy = PsychrometricCalculator.vbaRound(mixedAirPoint.enthalpy, 2);
                            const roundedSupplyEnthalpy = PsychrometricCalculator.vbaRound(supplyAirPoint.enthalpy, 2);

                            mixedAirEnthalpy = roundedMixedEnthalpy;
                            supplyAirEnthalpy = roundedSupplyEnthalpy;
                            enthalpyDiff = roundedMixedEnthalpy - roundedSupplyEnthalpy;
                          }
                          // 엔탈피 값이 없으면 온도/습도로 계산
                          else if (
                            mixedAirPoint.dryBulbTemp !== undefined && mixedAirPoint.relativeHumidity !== undefined &&
                            supplyAirPoint.dryBulbTemp !== undefined && supplyAirPoint.relativeHumidity !== undefined
                          ) {
                            try {
                              const mixedAirState = PsychrometricCalculator.calculate({
                                dryBulbTemp: mixedAirPoint.dryBulbTemp,
                                relativeHumidity: mixedAirPoint.relativeHumidity
                              });
                              const supplyAirState = PsychrometricCalculator.calculate({
                                dryBulbTemp: supplyAirPoint.dryBulbTemp,
                                relativeHumidity: supplyAirPoint.relativeHumidity
                              });

                              if (mixedAirState && supplyAirState &&
                                  mixedAirState.enthalpy !== undefined && supplyAirState.enthalpy !== undefined) {
                                // ✅ VBA 원본: Application.Round(EA1ent, 2) - Application.Round(LA1ent, 2)
                                // 엔탈피를 2자리 반올림한 후 차이 계산
                                const roundedMixedEnthalpy = PsychrometricCalculator.vbaRound(mixedAirState.enthalpy, 2);
                                const roundedSupplyEnthalpy = PsychrometricCalculator.vbaRound(supplyAirState.enthalpy, 2);

                                mixedAirEnthalpy = roundedMixedEnthalpy;
                                supplyAirEnthalpy = roundedSupplyEnthalpy;
                                enthalpyDiff = roundedMixedEnthalpy - roundedSupplyEnthalpy;
                              } else {
                                coolingCalculationError = 'PsychrometricCalculator로 엔탈피 계산 실패';
                              }
                            } catch (error) {
                              coolingCalculationError = 'PsychrometricCalculator 계산 중 오류 발생';
                            }
                          } else {
                            coolingCalculationError = '습공기선도 포인트에 엔탈피 또는 온도/습도 값이 없음';
                          }
                        } else {
                          coolingCalculationError = '습공기선도에서 혼합공기(MA) 또는 급기(SA) 포인트를 찾을 수 없음';
                        }
                      } else {
                        coolingCalculationError = '습공기선도 데이터가 없음';
                      }

                      // 엔탈피차 유효성 검사
                      if (coolingCalculationError === '' && (enthalpyDiff < 15 || enthalpyDiff > 35)) {
                        coolingCalculationError = `비정상적인 엔탈피차 (${enthalpyDiff.toFixed(1)}kJ/kg): 유효범위 15-35kJ/kg`;
                      }

                      // 냉각용량 계산
                      if (coolingCalculationError === '' && enthalpyDiff > 0) {
                        const coolingSafetyFactor = parseFloat(form.coolingSafetyFactor || form.safetyFactor) || 18.7;
                        const calculatedCoolingCapacity = supplyAirflow * 0.334 * enthalpyDiff * (1 + coolingSafetyFactor / 100);

                        if (calculatedCoolingCapacity > 0 && calculatedCoolingCapacity < 500000) {
                          deviceCoolingLoad = calculatedCoolingCapacity;
                        } else {
                          coolingCalculationError = `계산된 냉각용량이 비정상 (${calculatedCoolingCapacity.toFixed(0)}W): 유효범위 0-500,000W`;
                        }
                      }

                      // 계산 실패 시 0으로 설정
                      if (coolingCalculationError !== '') {
                        deviceCoolingLoad = 0;
                      }
                    }

                    // 난방 계산 - 습공기 선도 데이터 기반으로 일원화
                    if (effectivePsychroChartData && effectivePsychroChartData.points) {
                      const psychroPoints = effectivePsychroChartData.points;

                      const mixedAirPoint = psychroPoints.find(p => p.name === 'm1');
                      const heatingSupplyAirPoint = psychroPoints.find(p => p.name === 'l2');

                      if (mixedAirPoint && heatingSupplyAirPoint) {
                        if (mixedAirPoint.dryBulbTemp !== undefined && heatingSupplyAirPoint.dryBulbTemp !== undefined) {
                          mixedAirTemp = mixedAirPoint.dryBulbTemp;
                          supplyAirTemp = heatingSupplyAirPoint.dryBulbTemp;
                          tempDiff = supplyAirTemp - mixedAirTemp;

                          // 온도차 유효성 검사
                          if (tempDiff < 0 || tempDiff > 50) {
                            heatingCalculationError = `비정상적인 온도차 (${tempDiff.toFixed(1)}℃): 유효범위 0-50℃`;
                          } else if (tempDiff > 0) {
                            // 가열용량 계산: 화면 표시용과 동일하게 반올림하여 계산
                            const heatingSafetyFactor = parseFloat(form.heatingSafetyFactor || form.safetyFactor) || 10.0;

                            // 화면 표시용과 동일한 방식으로 반올림
                            const displaySupplyAirflow = Math.round(supplyAirflow * 10) / 10;
                            const displaySupplyTemp = Math.round(supplyAirTemp * 10) / 10;
                            const displayMixedTemp = Math.round(mixedAirTemp * 10) / 10;
                            const displayTempDiff = Math.round((displaySupplyTemp - displayMixedTemp) * 10) / 10;
                            const displaySafetyFactor = Math.round((1 + heatingSafetyFactor / 100) * 100) / 100;

                            const calculatedHeatingCapacity = Math.round(0.335 * displaySupplyAirflow * displayTempDiff * displaySafetyFactor);

                            if (calculatedHeatingCapacity > 0 && calculatedHeatingCapacity < 500000) {
                              deviceHeatingLoad = calculatedHeatingCapacity;
                            } else {
                              heatingCalculationError = `계산된 가열용량이 비정상 (${calculatedHeatingCapacity.toFixed(0)}W): 유효범위 0-500,000W`;
                            }
                          }
                        } else {
                          heatingCalculationError = '습공기선도 포인트에 온도 값이 없음';
                        }
                      } else {
                        heatingCalculationError = '습공기선도에서 m1 또는 l2 포인트를 찾을 수 없음';
                      }
                    } else {
                      heatingCalculationError = '습공기선도 데이터가 없음';
                    }

                    // 계산 실패 시 heatingLoad 폴백
                    if (heatingCalculationError !== '' || deviceHeatingLoad === 0) {
                      deviceHeatingLoad = heatingLoad || 0;
                    }

                    return {
                      deviceCoolingLoad,
                      deviceHeatingLoad,
                      mixedAirEnthalpy,
                      supplyAirEnthalpy,
                      enthalpyDiff,
                      mixedAirTemp,
                      supplyAirTemp,
                      tempDiff,
                      coolingCalculationError,
                      heatingCalculationError
                    };
                  };

                  // 통합 계산 실행
                  const deviceLoads = calculateDeviceLoads();
                  const deviceCoolingLoad = deviceLoads.deviceCoolingLoad;
                  const deviceHeatingLoad = deviceLoads.deviceHeatingLoad;

                  const deviceCoolingPerArea = area > 0 ? (deviceCoolingLoad / area).toFixed(2) : '';
                  const deviceHeatingPerArea = area > 0 ? (deviceHeatingLoad / area).toFixed(2) : '';


                  // 디버깅용 로그 - 전체 데이터 구조 확인
                  /*
                    'currentAHU 전체': currentAHU,
                    'currentAHU.psychroChartData': currentAHU.psychroChartData,
                    'currentAHU.psychroChartData?.points 개수': currentAHU.psychroChartData?.points?.length,
                    '급기량': supplyAirflow
                  });
                  */

                  /*
                  // 습공기 선도 포인트가 있다면 m1 값 확인
                  if (currentAHU.psychroChartData?.points) {
                    const m1Point = currentAHU.psychroChartData.points.find(p =>
                      p.name === 'm1' || p.name === 'M1' || p.name === '혼합공기(MA)'
                    );
                  }

                    '공조면적(m²)': area,
                    '천장고(m)': ceilingHeight,
                    '체적(m³)': volume,
                    '현열부하(W)': sensibleHeatLoad,
                    '잠열부하(W)': latentHeatLoad,
                    '환기조명부하(W)': ventilationLightingLoad,
                    '실내냉방부하합계(W)': sensibleHeatLoad + latentHeatLoad + ventilationLightingLoad,
                    '난방부하(W)': heatingLoad
                  });
                  */

                  // VBA 공식 실제 사용된 엔탈피 차이 확인 (안전율 포함)
                  const safetyFactorUsed = parseFloat(form.safetyFactor || form.coolingLoadSafetyFactor) || 18.7;
                  const actualEnthalpyDiff = deviceCoolingLoad / (supplyAirflow * 0.334 * (1 + safetyFactorUsed / 100));
                  const vbaFormulaResult = supplyAirflow * 0.334 * actualEnthalpyDiff * (1 + safetyFactorUsed / 100);

                  /*
                    'VBA_계산_공식': 'SAvol * 0.334 * (혼합공기엔탈피 - 급기엔탈피) * (1 + 안전율/100)',
                    '급기량(CMH)': supplyAirflow,
                    '적용된_안전율(%)': safetyFactorUsed,
                    '실제_사용된_엔탈피차이(kJ/kg)': actualEnthalpyDiff.toFixed(3),
                    'VBA공식_검증결과(W)': Math.round(vbaFormulaResult),
                    'calc.coolingCapacity': calc.coolingCapacity,
                    'currentAHU.coolingCapacity': currentAHU.coolingCapacity,
                    'ahu.coolingCapacity': ahu.coolingCapacity,
                    'calc.heatingCapacity': calc.heatingCapacity,
                    'currentAHU.heatingCapacity': currentAHU.heatingCapacity,
                    'ahu.heatingCapacity': ahu.heatingCapacity,
                    '최종_장치냉방부하(W)': deviceCoolingLoad,
                    '최종_장치난방부하(W)': deviceHeatingLoad,
                    '장치냉방부하(W/m²)': deviceCoolingPerArea,
                    '장치난방부하(W/m²)': deviceHeatingPerArea,
                    '기대값_비교': {
                      '기대_장치냉방부하(W)': 230528,
                      '기대_장치냉방부하(W/m²)': 274,
                      '계산_장치냉방부하(W)': deviceCoolingLoad,
                      '계산_장치냉방부하(W/m²)': deviceCoolingPerArea
                    }
                  });

                  /*
                    '급기량(CMH)': supplyAirflow,
                    '장치냉방부하(W/m²)': deviceCoolingPerArea,
                    '장치난방부하(W/m²)': deviceHeatingPerArea,
                    '급기풍량(m³/h/m²)': supplyAirPerArea,
                    '환기횟수(a.c/h)': airChange
                  });
                  */

                  // 재열 코일 용량 계산 (냉방용)
                  let reheatingCoilCapacityCooling = 0;
                  const reheatingCoilType = form.reheatingCoilType || 'None';
                  if (reheatingCoilType !== 'None' && effectivePsychroChartData?.points) {
                    const rhReheatTemp = parseFloat(form.rhReheatTemp) || 0;
                    const rhSafetyFactor = parseFloat(form.reheatingCoilSafetyFactor) || 10.0;
                    const supplyAirPointL1 = effectivePsychroChartData.points.find(p => p.name === 'l1');

                    if (rhReheatTemp > 0 && supplyAirPointL1?.dryBulbTemp !== undefined) {
                      const rhTempDiff = rhReheatTemp - supplyAirPointL1.dryBulbTemp;
                      reheatingCoilCapacityCooling = PsychrometricCalculator.vbaRoundUp(0.335 * supplyAirflow * rhTempDiff * (1 + rhSafetyFactor / 100), 0);
                    }
                  }

                  // 총 냉방 코일 용량 = 냉방 코일 용량 + 재열 코일 용량
                  const totalCoolingCoilCapacity = deviceCoolingLoad + reheatingCoilCapacityCooling;

                  // 냉방부하(%) 계산
                  let coolingRoomLoad = '';
                  let coolingOALoad = '';
                  let coolingAddFan = '';

                  if (deviceCoolingLoad > 0) {
                    // RA Lighting Load 적용 확인 (1. 공조 시스템 정보에서)
                    const isRALightingIgnored = form.raLightingLoadIgnored === true ||
                                              form.raLightingLoadApplication === 'ignore' ||
                                              form.raLightingLoadApplication === '무시';

                    // Room Load 계산: 냉방실내부하 / 총냉방코일용량 * 100
                    // VBA print_sarek2.bas:2130-2132: Application.Round((현열+잠열[+환기조명]) / Cells(Re,102) * 100, 2)
                    let coolingRoomLoadValue = 0;
                    if (isRALightingIgnored) {
                      // RA Lighting Load가 무시인 경우
                      coolingRoomLoadValue = PsychrometricCalculator.vbaRound((sensibleHeatLoad + latentHeatLoad) / totalCoolingCoilCapacity * 100, 2);
                    } else {
                      // 그 밖의 경우
                      coolingRoomLoadValue = PsychrometricCalculator.vbaRound((sensibleHeatLoad + latentHeatLoad + ventilationLightingLoad) / totalCoolingCoilCapacity * 100, 2);
                    }
                    coolingRoomLoad = coolingRoomLoadValue.toFixed(1);

                    // OA Load 계산: 냉방외기부하 / 총냉방코일용량 * 100
                    // VBA print_sarek2.bas:2134: Application.Round(Cells(Re,107) / Cells(Re,102) * 100, 2)

                    // localStorage에서 최신 계산 결과 가져오기
                    let outdoorCoolingLoadFromStorage = 0;
                    try {
                      const calcResults = JSON.parse(localStorage.getItem('calculationResults') || '{}');
                      outdoorCoolingLoadFromStorage = calcResults[currentAHU.id] || 0;
                    } catch (e) {
                      // localStorage 읽기 실패
                    }

                    const outdoorCoolingLoad = parseFloat(form.outdoorCoolingLoad) ||
                                              outdoorCoolingLoadFromStorage ||
                                              calc.outdoorCoolingLoad ||
                                              currentAHU.outdoorCoolingLoad || 0;
                    // console.log('🔍 [OA Load 계산]', {
                    //   'currentAHU.id': currentAHU.id,
                    //   'currentAHU.ahuNumber': currentAHU.ahuNumber,
                    //   'form.outdoorCoolingLoad': form.outdoorCoolingLoad,
                    //   'localStorage.outdoorCoolingLoad': outdoorCoolingLoadFromStorage,
                    //   'calc.outdoorCoolingLoad': calc.outdoorCoolingLoad,
                    //   'currentAHU.outdoorCoolingLoad': currentAHU.outdoorCoolingLoad,
                    //   'outdoorCoolingLoad (최종)': outdoorCoolingLoad,
                    //   totalCoolingCoilCapacity,
                    //   '나누기 결과': outdoorCoolingLoad / totalCoolingCoilCapacity,
                    //   '곱하기 100': outdoorCoolingLoad / totalCoolingCoilCapacity * 100,
                    //   'VBA 기대값 (35.9)을 위한 outdoorCoolingLoad': 35.9 * totalCoolingCoilCapacity / 100
                    // });
                    const coolingOALoadValue = PsychrometricCalculator.vbaRound(outdoorCoolingLoad / totalCoolingCoilCapacity * 100, 2);
                    // console.log('🔍 [OA Load 결과]', { coolingOALoadValue, 'toFixed(1)': coolingOALoadValue.toFixed(1) });
                    coolingOALoad = coolingOALoadValue.toFixed(1);

                    // Add & Fan 계산: 100 - Room Load - OA Load
                    // VBA print_sarek2.bas:2135: .Cells(P1 + 9, 14) = 100 - .Cells(P1 + 7, 14) - .Cells(P1 + 8, 14)
                    // VBA는 2자리 반올림된 값을 사용 (셀에 저장된 값)
                    const coolingAddFanValue = 100 - coolingRoomLoadValue - coolingOALoadValue;
                    coolingAddFan = coolingAddFanValue.toFixed(1);
                  }

                  // ============================================
                  // 냉방부하(%) 계산 공식 (VBA print_sarek2.bas 라인 2130-2135 기준)
                  // ============================================
                  // ah1: "ah-data" 워크시트
                  // Room Load(%):
                  //   - RA Lighting Load 무시(C) : (현열부하 + 잠열부하) / 장치냉방부하 × 100
                  //   - RA Lighting Load 적용    : (현열부하 + 잠열부하 + 환기조명부하) / 장치냉방부하 × 100
                  // OA Load(%)  : 냉방외기부하 / 장치냉방부하 × 100
                  // Add & Hum(%) : 100 - Room Load(%) - OA Load(%)
                  // ============================================

                  // ============================================
                  // 난방부하(%) 계산 공식 (VBA print_sarek2.bas 라인 2138-2140 기준)
                  // ============================================
                  // Room Load(%): 난방실내부하 / 장치난방부하 × 100
                  // OA Load(%)  : 난방외기부하 / 장치난방부하 × 100
                  // Add & Hum(%): 100 - Room Load(%) - OA Load(%)
                  //
                  // 여기서 장치난방부하 = P/H코일용량 + A/H코일용량 (VBA: Cells(Re,104) + Cells(Re,105))
                  // ============================================

                  // 난방부하(%) 계산 구현
                  let heatingRoomLoad = '';
                  let heatingOALoad = '';
                  let heatingAddHum = '';

                  // 예열코일 용량 계산 (VBA ahucode0.bas:740)
                  let preheatingCoilCapacity = 0;
                  const preheatingCoilType = form.preheatingCoilType || 'None';
                  if (preheatingCoilType !== 'None') {
                    const phPreheatingTemp = parseFloat(form.phPreheatingTemp) || 0;
                    const phSafetyFactor = parseFloat(form.preheatingCoilSafetyFactor) || 10.0;

                    if (phPreheatingTemp > 0 && effectivePsychroChartData?.points) {
                      const mixedAirPoint = effectivePsychroChartData.points.find(p => p.name === 'm1');
                      if (mixedAirPoint?.dryBulbTemp !== undefined) {
                        const mixedAirTemp = mixedAirPoint.dryBulbTemp;
                        const phTempDiff = phPreheatingTemp - mixedAirTemp;
                        preheatingCoilCapacity = PsychrometricCalculator.vbaRoundUp(0.335 * supplyAirflow * phTempDiff * (1 + phSafetyFactor / 100), 0);
                      }
                    }
                  }

                  // 난방 재열코일 용량 계산 (React는 VBA의 DDRH1이 없으므로 0)
                  const reheatingCoilCapacity = 0;
                  const totalHeatingCoilCapacity = preheatingCoilCapacity + deviceHeatingLoad;

                  if (deviceHeatingLoad > 0) {
                    // Room Load 계산: 난방실내부하 / 총난방코일용량 × 100
                    // VBA: Application.Round(Hth / (Cells(Re,104) + Cells(Re,105)) * 100, 2)
                    const roomLoadValue = PsychrometricCalculator.vbaRound(heatingLoad / totalHeatingCoilCapacity * 100, 2);
                    heatingRoomLoad = roomLoadValue.toFixed(1);

                    // OA Load 계산: 난방외기부하 / 총난방코일용량 × 100
                    // VBA: Application.Round(Cells(Re,108) / (Cells(Re,104) + Cells(Re,105)) * 100, 2)
                    // 총난방코일용량 = 예열코일용량(Cells 5,104) + 가열코일용량(Cells 5,105)
                    let outdoorHeatingLoad = 0;
                    if (effectivePsychroChartData?.points) {
                      const heatingReturnAirPoint = effectivePsychroChartData.points.find(p => p.name === 'r2');
                      const heatingMixedAirPoint = effectivePsychroChartData.points.find(p => p.name === 'm1');

                      if (heatingReturnAirPoint && heatingMixedAirPoint && supplyAirflow) {
                        // ✅ VBA 원본: RoundUp(0.335 * SAvol * (RAhdb - Round(EAh1db, 1)), 0)
                        // SAvol = 급기풍량, RAhdb = r2 환기온도, EAh1db = m1 난방 혼합공기온도
                        const returnAirTemp = heatingReturnAirPoint.dryBulbTemp;
                        const roundedMixedAirTemp = Math.round(heatingMixedAirPoint.dryBulbTemp * 10) / 10;
                        const tempDiff = returnAirTemp - roundedMixedAirTemp;

                        // VBA: 급기풍량(SAvol) 사용
                        const calculatedValue = 0.335 * supplyAirflow * tempDiff;
                        outdoorHeatingLoad = PsychrometricCalculator.vbaRoundUp(calculatedValue, 0);

                      }
                    }

                    // VBA: Application.Round(Cells(Re,108) / (Cells(Re,104) + Cells(Re,105)) * 100, 2)
                    const oaLoadValue = PsychrometricCalculator.vbaRound(outdoorHeatingLoad / totalHeatingCoilCapacity * 100, 2);
                    heatingOALoad = oaLoadValue.toFixed(1);

                    // Add & Hum 계산: 100 - Room Load - OA Load
                    // VBA print_sarek2.bas:2140: .Cells(P1 + 12, 14) = 100 - .Cells(P1 + 10, 14) - .Cells(P1 + 11, 14)
                    // VBA는 2자리 반올림된 값을 사용 (셀에 저장된 값)
                    const addHumValue = 100 - roomLoadValue - oaLoadValue;
                    heatingAddHum = addHumValue.toFixed(1);
                  }

                  // 표 헤더/구조 정의 (CSV/이미지 참고)
                  // 13열 기준
                  // 1행: 상단 정보
                  // 2~5행: 부하
                  // 6행: sub-header (인원수/㎥/h/인, RSHF 위 W)
                  // 7행: 헤더
                  // 8행: 값

                  return (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableBody>
                          {/* 1행: 상단 정보 */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ ...borderStyle, fontSize: 14 }}>
                              Zone Block Peak Time : {zoneBlockPeakTime}
                            </TableCell>
                            <TableCell colSpan={7} sx={{ ...borderStyle, fontSize: 14, textAlign: 'right' }}>
                              System : {systemType}, 외기 냉방 적용 : ○
                            </TableCell>
                          </TableRow>
                          {/* 2~4행: 실내냉방부하 */}
                          <TableRow>
                            <TableCell rowSpan={3} sx={{ ...borderStyle, fontWeight: 'bold', width: 150 }}>실내냉방부하</TableCell>
                            <TableCell colSpan={2} sx={borderStyle}>현열 :</TableCell>
                            <TableCell colSpan={2} sx={{...borderStyle, textAlign: 'right' }}>{sensibleHeatLoad.toLocaleString()}</TableCell>
                            <TableCell sx={borderStyle}>W</TableCell>
                            <TableCell  colSpan={2} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              장치냉방부하(W/㎡)
                            </TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg, textAlign: 'right' }}>{deviceCoolingPerArea}</TableCell>
                            <TableCell rowSpan={3} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              냉방부하<br/>(%)
                            </TableCell>
                            <TableCell colSpan={2}  sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>Room Load</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{coolingRoomLoad}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={borderStyle}>잠열 :</TableCell>
                            <TableCell colSpan={2} sx={{...borderStyle, textAlign: 'right' }}>{latentHeatLoad.toLocaleString()}</TableCell>
                            <TableCell sx={borderStyle}>W</TableCell>
                            <TableCell  colSpan={2} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              장치난방부하(W/㎡)
                            </TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg, textAlign: 'right' }}>{deviceHeatingPerArea}</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>OA Load</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{coolingOALoad}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={borderStyle}>천장속 부하(Q1) :</TableCell>
                            <TableCell colSpan={2} sx={{...borderStyle, textAlign: 'right' }}>{ventilationLightingLoad.toLocaleString()}</TableCell>
                            <TableCell sx={borderStyle}>W</TableCell>
                            <TableCell  colSpan={2} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              급기 풍량(㎥/h/㎡)
                            </TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg, textAlign: 'right' }}>{supplyAirPerArea}</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>Add & Fan</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{coolingAddFan}</TableCell>
                          </TableRow>
                          {/* 5,6,7행: 실내난방부하 */}
                          <TableRow>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold',  width: 150  }}>
                              실내난방부하
                            </TableCell>
                            <TableCell colSpan={2} sx={borderStyle}>전열 :</TableCell>
                            <TableCell colSpan={2} sx={{...borderStyle, textAlign: 'right' }}>{heatingLoad.toLocaleString()}</TableCell>
                            <TableCell sx={borderStyle}>W</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              환기 횟수(a.c/h)
                            </TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg, textAlign: 'right' }}>{airChange}</TableCell>
                            <TableCell rowSpan={3} sx={{ ...borderStyle, ...rightBg, fontWeight: 'bold', textAlign: 'center' }}>
                              난방부하<br/>(%)
                            </TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>
                              Room Load
                            </TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{heatingRoomLoad}</TableCell>
                          </TableRow>
                          {/* 6행: 헤더 */}
                          <TableRow>
                            <TableCell sx={{ ...borderStyle, textAlign: 'center', fontWeight: 'bold', width: 150  }}>배열회수기</TableCell>
                            <TableCell sx={{ ...borderStyle, textAlign: 'center', fontWeight: 'bold' }}>냉방효율</TableCell>
                            <TableCell sx={{ ...borderStyle, textAlign: 'center', fontWeight: 'bold' }}>난방효율</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold' }}>인원수</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold' }}>㎥/h/인</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold' }}>RSHF</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>면적(㎡)</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>천장고(m)</TableCell>
                            <TableCell sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>체적(㎥)</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>OA Load</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{heatingOALoad}</TableCell>
                          </TableRow>
                          {/* 7행: 값 */}
                          <TableRow>
                            <TableCell sx={{...borderStyle, width: 150 }}>{form.heatRecoveryType || 'None'}</TableCell>
                            <TableCell sx={{ ...borderStyle, textAlign: 'center' }}>
                              {form.heatRecoveryType !== 'None' ? (form.coolingRecoveryEfficiency || '70') : '-'}
                            </TableCell>
                            <TableCell sx={{ ...borderStyle, textAlign: 'center' }}>
                              {form.heatRecoveryType !== 'None' ? (form.heatingRecoveryEfficiency || '70') : '-'}
                            </TableCell>
                            <TableCell sx={borderStyle}>{personnel}</TableCell>
                            <TableCell sx={borderStyle}>{parseFloat(personnelPerHour).toFixed(1)}</TableCell>
                            <TableCell sx={borderStyle}>{rshf.toFixed(2)}</TableCell>
                            <TableCell sx={{ ...borderStyle, ...rightBg }}>{area}</TableCell>
                            <TableCell sx={{ ...borderStyle, ...rightBg }}>{ceilingHeight}</TableCell>
                            <TableCell sx={{ ...borderStyle, ...rightBg }}>{volumeFormatted}</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, ...rightBg, textAlign: 'center' }}>Add & Hum</TableCell>
                            <TableCell colSpan={2} sx={{ ...borderStyle, fontWeight: 'bold', ...rightBg }}>{heatingAddHum}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()}
              </Box>

              {/* 외기/실내 설계 조건 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  □ 외기/실내 설계 조건
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {(() => {
                        // 실제 외기/실내 조건 값들 가져오기
                        const currentAHUData = currentProjectAHUs?.find(projectAhu =>
                          projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
                        ) || {};
                        const formData = currentAHUData.formData || {};

                        const outdoorCoolingTemp = parseFloat(formData.outdoorCoolingTemp) ||
                                                  ahuCalculationResults?.outdoorAir?.dryBulbTemp || 31.2;
                        const outdoorCoolingHumidity = parseFloat(formData.outdoorCoolingHumidity) ||
                                                     ahuCalculationResults?.outdoorAir?.relativeHumidity || 69.0;
                        const indoorCoolingTemp = parseFloat(formData.coolingRoomTemp) ||
                                                ahuCalculationResults?.returnAir?.dryBulbTemp || 26.0;
                        const indoorCoolingHumidity = parseFloat(formData.coolingRoomHumidity) ||
                                                    ahuCalculationResults?.returnAir?.relativeHumidity || 55.0;

                        const outdoorHeatingTemp = parseFloat(formData.outdoorHeatingTemp) || -11.9;
                        const outdoorHeatingHumidity = parseFloat(formData.outdoorHeatingHumidity) || 69.0;
                        const indoorHeatingTemp = parseFloat(formData.heatingRoomTemp) || 20.0;
                        const indoorHeatingHumidity = parseFloat(formData.heatingRoomHumidity) || 40.0;

                        // ============================================================================
                        // ⚠️ 중요: VBA와 동일한 방식으로 엔탈피 계산 (절대습도 Round 적용 필수)
                        // ============================================================================
                        // VBA 계산 순서 (ahucode0.bas:833-836):
                        // 1. RAabs = xfuc!(RAdb, RArh, Z)           - 절대습도 계산
                        // 2. RAent = entfuc!(RAdb, Application.Round(RAabs, 4))  - 엔탈피 계산
                        //
                        // 주의사항:
                        // - 엔탈피 계산 시 절대습도를 반드시 Application.Round(x, 4)로 반올림해야 함
                        // - PsychrometricCalculator.calculate()를 직접 사용하면 안됨 (반올림 없이 계산됨)
                        // - 반올림하지 않으면 VBA와 엔탈피 값이 달라짐
                        // ============================================================================

                        const elevation = parseFloat(formData.elevation) || 0;

                        // 냉방 외기 조건
                        const outdoorCoolingHR = PsychrometricCalculator.calculateHumidityRatio(outdoorCoolingTemp, outdoorCoolingHumidity, elevation);
                        const outdoorCoolingHRRounded = PsychrometricCalculator.vbaRound(outdoorCoolingHR, 4);  // 반올림 필수!
                        const outdoorCoolingEnthalpy = PsychrometricCalculator.calculateEnthalpy(outdoorCoolingTemp, outdoorCoolingHRRounded);

                        // 냉방 실내 조건
                        const indoorCoolingHR = PsychrometricCalculator.calculateHumidityRatio(indoorCoolingTemp, indoorCoolingHumidity, elevation);
                        const indoorCoolingHRRounded = PsychrometricCalculator.vbaRound(indoorCoolingHR, 4);  // 반올림 필수!
                        const indoorCoolingEnthalpy = PsychrometricCalculator.calculateEnthalpy(indoorCoolingTemp, indoorCoolingHRRounded);

                        // 난방 외기 조건
                        const outdoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(outdoorHeatingTemp, outdoorHeatingHumidity, elevation);
                        const outdoorHeatingHRRounded = PsychrometricCalculator.vbaRound(outdoorHeatingHR, 4);  // 반올림 필수!
                        const outdoorHeatingEnthalpy = PsychrometricCalculator.calculateEnthalpy(outdoorHeatingTemp, outdoorHeatingHRRounded);

                        // 난방 실내 조건
                        const indoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(indoorHeatingTemp, indoorHeatingHumidity, elevation);
                        const indoorHeatingHRRounded = PsychrometricCalculator.vbaRound(indoorHeatingHR, 4);  // 반올림 필수!
                        const indoorHeatingEnthalpy = PsychrometricCalculator.calculateEnthalpy(indoorHeatingTemp, indoorHeatingHRRounded);


                        return (
                          <>
                            <TableRow>
                              <TableCell rowSpan={2} sx={{ backgroundColor: '#ffe6e6', fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle', width: '15%' }}>
                                Cooling
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>외기</TableCell>
                              <TableCell>
                                {outdoorCoolingTemp.toFixed(1)} ℃ DB&nbsp;&nbsp;&nbsp;&nbsp;{outdoorCoolingHumidity.toFixed(1)} % RH&nbsp;&nbsp;&nbsp;&nbsp;{outdoorCoolingHRRounded.toFixed(4)} kg/kg'&nbsp;&nbsp;&nbsp;&nbsp;{outdoorCoolingEnthalpy.toFixed(2)} kJ/kg
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>실내</TableCell>
                              <TableCell>
                                {indoorCoolingTemp.toFixed(1)} ℃ DB&nbsp;&nbsp;&nbsp;&nbsp;{indoorCoolingHumidity.toFixed(1)} % RH&nbsp;&nbsp;&nbsp;&nbsp;{indoorCoolingHRRounded.toFixed(4)} kg/kg'&nbsp;&nbsp;&nbsp;&nbsp;{indoorCoolingEnthalpy.toFixed(2)} kJ/kg
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell rowSpan={2} sx={{ backgroundColor: '#e6f3ff', fontWeight: 'bold', textAlign: 'center', verticalAlign: 'middle', width: '15%' }}>
                                Heating
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>외기</TableCell>
                              <TableCell>
                                {outdoorHeatingTemp.toFixed(1)} ℃ DB&nbsp;&nbsp;&nbsp;&nbsp;{outdoorHeatingHumidity.toFixed(1)} % RH&nbsp;&nbsp;&nbsp;&nbsp;{outdoorHeatingHRRounded.toFixed(4)} kg/kg'&nbsp;&nbsp;&nbsp;&nbsp;{outdoorHeatingEnthalpy.toFixed(2)} kJ/kg
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>실내</TableCell>
                              <TableCell>
                                {indoorHeatingTemp.toFixed(1)} ℃ DB&nbsp;&nbsp;&nbsp;&nbsp;{indoorHeatingHumidity.toFixed(1)} % RH&nbsp;&nbsp;&nbsp;&nbsp;{indoorHeatingHRRounded.toFixed(4)} kg/kg'&nbsp;&nbsp;&nbsp;&nbsp;{indoorHeatingEnthalpy.toFixed(2)} kJ/kg
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* AIR VOLUME SELECTION */}
              {(() => {
                // 데이터 추출 (중복 정의)
                const currentAHUData = currentProjectAHUs?.find(projectAhu =>
                  projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
                ) || {};

                const formData = currentAHUData.formData || {};
                const calcData = currentAHUData.calculationResults || {};

                // 주요 값들 재계산
                const personnelCount = parseInt(formData.personnel || formData.occupantCount) || 0;
                const personnelAirflowRate = parseFloat(formData.airflowPerPerson || formData.personnelPerHour) || 25;
                const sensibleLoad = parseFloat(formData.sensibleCoolingLoad) || 0;
                const latentLoad = parseFloat(formData.latentCoolingLoad) || 0;
                const ventilationLightingLoad = parseFloat(formData.ventilationLightingLoad) || 0;
                const heatingLoad = parseFloat(formData.heatingLoad) || 0;
                const supplyTempDelta = parseFloat(formData.supplyTempDelta) || 10; // 풍량 선정 탭의 급기(△T) 값

                // 급기량 계산
                let calculatedSupplyAirflow =
                  calcData.calculatedAirflows?.supplyAirflow ||
                  currentAHUData.calculatedAirflows?.supplyAirflow ||
                  calcData.supplyAirflow ||
                  parseFloat(formData.directSupplyAirflow) ||
                  parseFloat(formData.supplyAirflow) ||
                  ahu.airVolume ||
                  ahu.fanAirflow;

                // 급기량 선정 기준
                const supplyAirSelectionMethod = formData.supplyAirSelectionCriteria || 'standard';

                // 급기량 계산식 생성
                let supplyCalcFormula = '';
                if (supplyAirSelectionMethod === 'direct') {
                  supplyCalcFormula = `직접 입력: ${PsychrometricCalculator.vbaRoundUp(calculatedSupplyAirflow, 0).toLocaleString()} ㎥/h`;
                } else {
              
                  // VBA: Application.RoundUp((Csh0 + 환기조명부하) / (0.335 * ah1.Cells(Re, 57)), 0)
                  // VBA ahucode0.bas:2126-2127 공식:급기량 = (냉방 현열 부하 + 환기 조명 부하) / (0.335 × △T)
                  const totalLoad = sensibleLoad + ventilationLightingLoad;
                  const roundedSupplyAirflow = PsychrometricCalculator.vbaRoundUp(totalLoad / (0.335 * supplyTempDelta), 0);
                  supplyCalcFormula = `(${sensibleLoad.toLocaleString()} + ${ventilationLightingLoad.toLocaleString()}) W ÷ (0.335 × ${supplyTempDelta} ℃) = ${roundedSupplyAirflow.toLocaleString()} ㎥/h`;
                  calculatedSupplyAirflow = PsychrometricCalculator.vbaRoundUp(roundedSupplyAirflow, -2);
                }

                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" component="div" sx={{
                      backgroundColor: '#000',
                      color: 'white',
                      p: 1,
                      fontWeight: 'bold'
                    }}>
                      ■ AIR VOLUME SELECTION
                    </Typography>

                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f0fff0', fontWeight: 'bold', width: '15%' }}>
                              Supply Air Volume
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                              {Math.round(calculatedSupplyAirflow).toLocaleString()} ㎥/h
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {supplyCalcFormula}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f0fff0', fontWeight: 'bold', width: '15%' }}>
                              Outdoor Air Volume
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                              {(() => {                                
                                const outdoorAirflow = calcData.outdoorAirflow;
                                return Math.round(outdoorAirflow).toLocaleString();
                              })()} ㎥/h
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {personnelCount} p × {personnelAirflowRate} ㎥/h/p = {Math.round(personnelCount * personnelAirflowRate).toLocaleString()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f0fff0', fontWeight: 'bold', width: '15%' }}>
                              Exhaust Air Volume
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                              {(() => {
                                // VBA 원본 출력시트: EAvol = ahdata.Cells(Re, 43) + ahdata.Cells(Re, 44) (배기량 + 가압량)
                                const exhaustAirflow = parseFloat(formData.exhaustAirflow) || 0; // 배기량
                                const pressurizationAirflow = parseFloat(formData.pressurizationAirflow) || 0; // 가압량
                                const exhaustAirVolume = exhaustAirflow + pressurizationAirflow;
                                return Math.round(exhaustAirVolume).toLocaleString();
                              })()} ㎥/h
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              배기량({Math.round(parseFloat(formData.exhaustAirflow) || 0).toLocaleString()}) + 가압량({Math.round(parseFloat(formData.pressurizationAirflow) || 0).toLocaleString()})
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}

              {/* COOLING / HEATING COIL & HUMIDIFIER CAPACITY */}
              {(() => {
                // 데이터 추출 (중복 정의)
                const currentAHUData = currentProjectAHUs?.find(projectAhu =>
                  projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
                ) || {};

                const formData = currentAHUData.formData || {};
                const calcData = currentAHUData.calculationResults || {};

                // 주요 값들 재계산
                const personnelCount = parseInt(formData.personnel || formData.occupantCount) || 0;
                const personnelAirflowRate = parseFloat(formData.airflowPerPerson || formData.personnelPerHour) || 25;
                const sensibleLoad = parseFloat(formData.sensibleCoolingLoad) || 0;
                const latentLoad = parseFloat(formData.latentCoolingLoad) || 0;
                const ventilationLightingLoad = parseFloat(formData.ventilationLightingLoad) || 0;

                // 급기량 계산
                const calculatedSupplyAirflow =
                  calcData.calculatedAirflows?.supplyAirflow ||
                  currentAHUData.calculatedAirflows?.supplyAirflow ||
                  calcData.supplyAirflow ||
                  parseFloat(formData.directSupplyAirflow) ||
                  parseFloat(formData.supplyAirflow) ||
                  ahu.airVolume ||
                  ahu.fanAirflow ||
                  (sensibleLoad > 0 ? Math.round(sensibleLoad / (0.335 * 10)) : 0);

                // ===== 통합 계산된 장치부하 사용 (중복 계산 제거) =====
                // 통합 계산 함수를 다시 호출하여 모든 계산 결과 가져오기
                const calculationDetails = ((formData, calcData, currentAHUData, ahu, calculatedSupplyAirflow) => {
                  let deviceCoolingLoad = 0;
                  let deviceHeatingLoad = 0;
                  let mixedAirEnthalpy = 0;
                  let supplyAirEnthalpy = 0;
                  let enthalpyDiff = 0;
                  let mixedAirTemp = 0;
                  let supplyAirTemp = 0;
                  let tempDiff = 0;
                  let coolingCalculationError = '';
                  let heatingCalculationError = '';
                  let coolingSafetyFactor = 18.7;
                  let heatingSafetyFactor = 10.0;

                  // psychroChartData가 없으면 calculationResults에서 합성 (저장 파일 로드 시 폴백)
                  const effectivePsychroChartData = resolvePsychroChartData(currentAHUData);

                  // 냉방 계산
                  if (calcData.coolingCapacity) {
                    deviceCoolingLoad = calcData.coolingCapacity;
                  } else if (currentAHUData.coolingCapacity) {
                    deviceCoolingLoad = currentAHUData.coolingCapacity;
                  } else if (ahu.coolingCapacity) {
                    deviceCoolingLoad = ahu.coolingCapacity;
                  } else {
                    // 습공기 선도에서 엔탈피 값 계산
                    if (effectivePsychroChartData && effectivePsychroChartData.points) {
                      const psychroPoints = effectivePsychroChartData.points;

                      // 혼합공기 포인트 찾기 (냉방: 'ma')
                      const mixedAirPoint = psychroPoints.find(p => p.name === 'ma');

                      // 냉각후 급기 포인트 찾기 (정확한 이름: 'l1')
                      const supplyAirPoint = psychroPoints.find(p => p.name === 'l1');


                      if (mixedAirPoint && supplyAirPoint) {
                        // 습공기 선도에 이미 계산된 엔탈피 값이 있으면 그대로 사용 (VBA 정확도 유지)
                        if (mixedAirPoint.enthalpy !== undefined && supplyAirPoint.enthalpy !== undefined) {
                          // ✅ VBA 원본: Application.Round(EA1ent, 2) - Application.Round(LA1ent, 2)
                          // 엔탈피를 2자리 반올림한 후 차이 계산
                          const roundedMixedEnthalpy = PsychrometricCalculator.vbaRound(mixedAirPoint.enthalpy, 2);
                          const roundedSupplyEnthalpy = PsychrometricCalculator.vbaRound(supplyAirPoint.enthalpy, 2);

                          mixedAirEnthalpy = roundedMixedEnthalpy;
                          supplyAirEnthalpy = roundedSupplyEnthalpy;
                          enthalpyDiff = roundedMixedEnthalpy - roundedSupplyEnthalpy;
                        }
                        // 엔탈피가 없으면 건구온도와 상대습도로 계산 (fallback)
                        else if (
                          mixedAirPoint.dryBulbTemp !== undefined && mixedAirPoint.relativeHumidity !== undefined &&
                          supplyAirPoint.dryBulbTemp !== undefined && supplyAirPoint.relativeHumidity !== undefined
                        ) {
                          try {
                            mixedAirPoint.humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
                              mixedAirPoint.dryBulbTemp,
                              mixedAirPoint.relativeHumidity);

                            mixedAirEnthalpy = PsychrometricCalculator.calculateEnthalpy(
                              mixedAirPoint.dryBulbTemp,
                              mixedAirPoint.humidityRatio
                            );

                            supplyAirPoint.humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
                              supplyAirPoint.dryBulbTemp,
                              supplyAirPoint.relativeHumidity);

                            supplyAirEnthalpy = PsychrometricCalculator.calculateEnthalpy(
                              supplyAirPoint.dryBulbTemp,
                              supplyAirPoint.humidityRatio
                            );
                            enthalpyDiff = mixedAirEnthalpy - supplyAirEnthalpy;
                          } catch (error) {
                            coolingCalculationError = '건구온도와 절대습도로 엔탈피 계산 중 오류 발생';
                          }
                        } else {
                          coolingCalculationError = '습공기선도 포인트에 엔탈피 또는 온도/습도 값이 없음';
                        }
                      } else {
                        coolingCalculationError = '습공기선도에서 혼합공기(MA) 또는 급기(SA) 포인트를 찾을 수 없음';
                      }
                    } else {
                      coolingCalculationError = '습공기선도 데이터가 없음';
                    }

                    // 엔탈피차 유효성 검사
                    if (coolingCalculationError === '' && (enthalpyDiff < 15 || enthalpyDiff > 35)) {
                      coolingCalculationError = `비정상적인 엔탈피차 (${enthalpyDiff.toFixed(1)}kJ/kg): 유효범위 15-35kJ/kg`;
                    }

                    // 냉각용량 계산
                    if (coolingCalculationError === '' && enthalpyDiff > 0) {
                      coolingSafetyFactor = parseFloat(formData.coolingSafetyFactor);
                      // VBA: Application.RoundUp(SAvol * 0.334 * (Round(EA1ent, 2) - Round(LA1ent, 2)) * (1 + safetyFactor / 100), 0)
                      const calculatedCoolingCapacity = calculatedSupplyAirflow * 0.334 * enthalpyDiff * (1 + coolingSafetyFactor / 100);
                      const roundedCoolingCapacity = PsychrometricCalculator.vbaRoundUp(calculatedCoolingCapacity, 0);

                      if (roundedCoolingCapacity > 0 && roundedCoolingCapacity < 500000) {
                        deviceCoolingLoad = roundedCoolingCapacity;
                      } else {
                        coolingCalculationError = `계산된 냉각용량이 비정상 (${roundedCoolingCapacity.toFixed(0)}W): 유효범위 0-500,000W`;
                      }
                    }

                    // 계산 실패 시 0으로 설정
                    if (coolingCalculationError !== '') {
                      deviceCoolingLoad = 0;
                    }
                  }

                  // 난방 계산 - 습공기 선도 데이터 기반으로 일원화
                  if (effectivePsychroChartData && effectivePsychroChartData.points) {
                    const psychroPoints = effectivePsychroChartData.points;

                    const mixedAirPoint = psychroPoints.find(p => p.name === 'm1');
                    const heatingSupplyAirPoint = psychroPoints.find(p => p.name === 'l2');

                    if (mixedAirPoint && heatingSupplyAirPoint) {
                      if (mixedAirPoint.dryBulbTemp !== undefined && heatingSupplyAirPoint.dryBulbTemp !== undefined) {
                        mixedAirTemp = mixedAirPoint.dryBulbTemp;
                        supplyAirTemp = heatingSupplyAirPoint.dryBulbTemp;
                        tempDiff = supplyAirTemp - mixedAirTemp;

                        // 온도차 유효성 검사
                        if (tempDiff < 0 || tempDiff > 50) {
                          heatingCalculationError = `비정상적인 온도차 (${tempDiff.toFixed(1)}℃): 유효범위 0-50℃`;
                        } else if (tempDiff > 0) {
                          // 가열용량 계산: 화면 표시용과 동일하게 반올림하여 계산
                          heatingSafetyFactor = parseFloat(formData.heatingSafetyFactor || formData.safetyFactor) || 10.0;

                          // 화면 표시용과 동일한 방식으로 반올림
                          const displaySupplyAirflow = Math.round(calculatedSupplyAirflow * 10) / 10;
                          const displaySupplyTemp = Math.round(supplyAirTemp * 10) / 10;
                          const displayMixedTemp = Math.round(mixedAirTemp * 10) / 10;
                          const displayTempDiff = Math.round((displaySupplyTemp - displayMixedTemp) * 10) / 10;
                          const displaySafetyFactor = Math.round((1 + heatingSafetyFactor / 100) * 100) / 100;

                          const calculatedHeatingCapacity = Math.round(0.335 * displaySupplyAirflow * displayTempDiff * displaySafetyFactor);

                          if (calculatedHeatingCapacity > 0 && calculatedHeatingCapacity < 500000) {
                            deviceHeatingLoad = calculatedHeatingCapacity;
                          } else {
                            heatingCalculationError = `계산된 가열용량이 비정상 (${calculatedHeatingCapacity.toFixed(0)}W): 유효범위 0-500,000W`;
                          }
                        }
                      } else {
                        heatingCalculationError = '습공기선도 포인트에 온도 값이 없음';
                      }
                    } else {
                      heatingCalculationError = '습공기선도에서 m1 또는 l2 포인트를 찾을 수 없음';
                    }
                  } else {
                    heatingCalculationError = '습공기선도 데이터가 없음';
                  }

                  // 계산 실패 시 heatingLoad 폴백
                  if (heatingCalculationError !== '' || deviceHeatingLoad === 0) {
                    deviceHeatingLoad = parseFloat(formData.heatingLoad) || 0;
                  }

                  return {
                    deviceCoolingLoad,
                    deviceHeatingLoad,
                    mixedAirEnthalpy,
                    supplyAirEnthalpy,
                    enthalpyDiff,
                    mixedAirTemp,
                    supplyAirTemp,
                    tempDiff,
                    coolingCalculationError,
                    heatingCalculationError,
                    coolingSafetyFactor,
                    heatingSafetyFactor
                  };
                })(formData, calcData, currentAHUData, ahu, calculatedSupplyAirflow);

                const calculatedDeviceCoolingLoad = calculationDetails.deviceCoolingLoad;
                const calculatedDeviceHeatingLoad = calculationDetails.deviceHeatingLoad;
                const mixedAirEnthalpy = calculationDetails.mixedAirEnthalpy;
                const supplyAirEnthalpy = calculationDetails.supplyAirEnthalpy;
                const enthalpyDiff = calculationDetails.enthalpyDiff;
                const mixedAirTemp = calculationDetails.mixedAirTemp;
                const supplyAirTemp = calculationDetails.supplyAirTemp;
                const tempDiff = calculationDetails.tempDiff;
                const coolingCalculationError = calculationDetails.coolingCalculationError;
                const heatingCalculationError = calculationDetails.heatingCalculationError;
                const calculatedCoolingSafetyFactor = calculationDetails.coolingSafetyFactor;
                const calculatedHeatingSafetyFactor = calculationDetails.heatingSafetyFactor;

                let calculatedHumidifierLoad = 0;
                let indoorHumidityRatio = 0;
                let outdoorHumidityRatio = 0;

                // 냉방/난방 계산은 이미 완료됨 (통합 계산 함수에서)

                // 가습: 가습기 용량 계산 (난방 조건 사용)
                // '외기/실내 설계 조건'의 Heating 실내/외기 습도비를 사용
                // VBA 방식으로 계산: 건구온도 + 상대습도 → 절대습도 → vbaRound(4자리) → 사용

                const elevation = parseFloat(formData.elevation) || 0;

                // 난방 실내 조건
                const indoorHeatingTemp = parseFloat(formData.heatingRoomTemp) || 20.0;
                const indoorHeatingHumidity = parseFloat(formData.heatingRoomHumidity) || 40.0;
                const indoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(indoorHeatingTemp, indoorHeatingHumidity, elevation);
                indoorHumidityRatio = PsychrometricCalculator.vbaRound(indoorHeatingHR, 4);

                // 난방 외기 조건
                const outdoorHeatingTemp = parseFloat(formData.outdoorHeatingTemp) || -11.9;
                const outdoorHeatingHumidity = parseFloat(formData.outdoorHeatingHumidity) || 70.0;
                const outdoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(outdoorHeatingTemp, outdoorHeatingHumidity, elevation);
                outdoorHumidityRatio = PsychrometricCalculator.vbaRound(outdoorHeatingHR, 4);

                // 가습기 용량 계산: 1.2 × 외기량 × (실내습도비 - 외기습도비) × 안전율
                // 가습 용량(kg/h) = 1.2 × 외기량(㎥/h) × 습도비차(kg/kg') × 안전율
                const outdoorAirflow = parseFloat(formData.directOutdoorAirflow) || (personnelCount * personnelAirflowRate) || 8700;
                const humidityRatioDiff = indoorHumidityRatio - outdoorHumidityRatio;
                const humidifierSafetyFactor = parseFloat(formData.humidifierSafetyFactor) || 10.0;

                // 수식 표시를 위해 항상 계산하되, 음수면 0으로 처리
                calculatedHumidifierLoad = 1.2 * outdoorAirflow * humidityRatioDiff * (1 + humidifierSafetyFactor / 100);

                // 음수는 0으로 처리 (가습 불필요)
                if (calculatedHumidifierLoad < 0) {
                  calculatedHumidifierLoad = 0;
                } else {
                  // VBA와 동일하게 올림 처리 (Application.RoundUp(..., 0))
                  calculatedHumidifierLoad = PsychrometricCalculator.vbaRoundUp(calculatedHumidifierLoad, 0);
                }

                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" component="div" sx={{
                      backgroundColor: '#000',
                      color: 'white',
                      p: 1,
                      fontWeight: 'bold'
                    }}>
                      ■ COOLING / HEATING COIL & HUMIDIFIER CAPACITY
                    </Typography>

                    {/* 냉방용 코일 */}
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mt: 2, mb: 1, color: '#1976d2' }}>
                      □ 냉방용
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, mb: 2 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold', width: '15%' }}>
                              재열
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const reheatingCoilType = formData.reheatingCoilType || 'None';
                                const rhReheatTemp = parseFloat(formData.rhReheatTemp) || 0;
                                const reheatingHeatCapacity = parseFloat(formData.reheatingHeatCapacity) || 0;

                                if (reheatingCoilType === 'None') {
                                  return <div style={{ color: '#999' }}>재열 코일 없음 (None)</div>;
                                }

                                const rhSafetyFactor = parseFloat(formData.reheatingCoilSafetyFactor) || calculatedHeatingSafetyFactor;

                                // Steam 타입: 열용량(kJ/kg) 사용
                                if (reheatingCoilType === 'Steam') {
                                  if (reheatingHeatCapacity === 0) {
                                    return (
                                      <div style={{ color: '#ff9800' }}>
                                        ⚠️ 재열 코일 타입: Steam (열용량 미설정)
                                      </div>
                                    );
                                  }

                                  // Steam 코일 용량 = 풍량(kg/s) × 열용량(kJ/kg) × 안전율
                                  const airflowKgS = (calculatedSupplyAirflow / 3600) * 1.2;
                                  const rhLoad = PsychrometricCalculator.vbaRoundUp(airflowKgS * reheatingHeatCapacity * (1 + rhSafetyFactor / 100) * 1000, 0);

                                  return (
                                    <div>
                                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                        🔍 Steam 타입 - 열용량: {reheatingHeatCapacity} kJ/kg
                                      </div>
                                      ({Math.round(calculatedSupplyAirflow).toLocaleString()} ㎥/h ÷ 3600 × 1.2) kg/s × {reheatingHeatCapacity} kJ/kg × {((1 + rhSafetyFactor / 100)).toString().replace(/\.?0+$/, '')} ＝
                                      <strong style={{ color: '#f57c00', fontSize: '1.1rem' }}>
                                        {rhLoad.toLocaleString()} W
                                      </strong>
                                    </div>
                                  );
                                }

                                // Water/Elec 타입: 온도 차이 사용
                                if (rhReheatTemp === 0) {
                                  return (
                                    <div style={{ color: '#ff9800' }}>
                                      ⚠️ 재열 코일 타입: {reheatingCoilType} (재열 온도 미설정)
                                    </div>
                                  );
                                }

                                // 재열 코일 용량 = 0.335 × 급기풍량 × (재열 후 온도 - 냉각 후 온도) × 안전율
                                // 냉각 후 온도 = 급기온도 (l1 포인트)
                                const rhTempDiff = rhReheatTemp - supplyAirTemp;
                                const rhLoad = PsychrometricCalculator.vbaRoundUp(0.335 * calculatedSupplyAirflow * rhTempDiff * (1 + rhSafetyFactor / 100), 0);

                                return (
                                  <div>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                      🔍 재열 온도: {supplyAirTemp.toFixed(1)}℃ → {rhReheatTemp.toFixed(1)}℃
                                    </div>
                                    0.335 × {Math.round(calculatedSupplyAirflow).toLocaleString()} ㎥/h × ({rhReheatTemp.toFixed(1)} - {supplyAirTemp.toFixed(1)}) ℃ × {((1 + rhSafetyFactor / 100)).toString().replace(/\.?0+$/, '')} ＝
                                    <strong style={{ color: '#f57c00', fontSize: '1.1rem' }}>
                                      {rhLoad.toLocaleString()} W
                                    </strong>
                                  </div>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#ffe6e6', fontWeight: 'bold' }}>
                              냉방
                            </TableCell>
                            <TableCell>
                              {coolingCalculationError ? (
                                <div>
                                  <div style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '8px' }}>
                                    ❌ 계산 실패
                                  </div>
                                  <div style={{ color: '#666', fontSize: '0.9rem', backgroundColor: '#ffebee', padding: '8px', borderRadius: '4px' }}>
                                    원인: {coolingCalculationError}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                    🔍 사용된 엔탈피: ma={mixedAirEnthalpy.toFixed(2)}kJ/kg, l1={supplyAirEnthalpy.toFixed(2)}kJ/kg
                                  </div>
                                  0.334 × {Math.round(calculatedSupplyAirflow).toLocaleString()} ㎥/h × ({mixedAirEnthalpy.toFixed(2)} - {supplyAirEnthalpy.toFixed(2)}) kJ/kg × {((1 + calculatedCoolingSafetyFactor / 100)).toString().replace(/\.?0+$/, '')} ＝
                                  <strong style={{ color: '#1976d2', fontSize: '1.1rem' }}>
                                    {Math.round(calculatedDeviceCoolingLoad).toLocaleString()} W
                                  </strong>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* 난방용 코일 및 가습기 */}
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mt: 2, mb: 1, color: '#d32f2f' }}>
                      □ 난방용
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#e6f3ff', fontWeight: 'bold', width: '15%' }}>
                              예열
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const preheatingCoilType = formData.preheatingCoilType || 'None';
                                const phPreheatingTemp = parseFloat(formData.phPreheatingTemp) || 0;
                                const preheatingHeatCapacity = parseFloat(formData.preheatingHeatCapacity) || 0;
                                const outdoorHeatingTemp = parseFloat(formData.outdoorHeatingTemp) || -11.9;

                                if (preheatingCoilType === 'None') {
                                  return <div style={{ color: '#999' }}>예열 코일 없음 (None)</div>;
                                }

                                const phSafetyFactor = parseFloat(formData.preheatingCoilSafetyFactor) || calculatedHeatingSafetyFactor;

                                // Water/Elec/Steam 타입 공통: 온도 차이 사용
                                // VBA: 0.335 × 급기풍량 × (예열 후 온도 - 혼합공기온도) × 안전율
                                if (phPreheatingTemp === 0) {
                                  return (
                                    <div style={{ color: '#ff9800' }}>
                                      ⚠️ 예열 코일 타입: {preheatingCoilType} (예열 온도 미설정)
                                    </div>
                                  );
                                }

                                // 예열 코일 용량 = 0.335 × 급기풍량 × (예열 후 온도 - 혼합공기온도) × 안전율
                                // 혼합공기온도 = 난방 시 혼합공기 온도 (m1 포인트)
                                const phTempDiff = phPreheatingTemp - mixedAirTemp;
                                const phLoad = PsychrometricCalculator.vbaRoundUp(0.335 * calculatedSupplyAirflow * phTempDiff * (1 + phSafetyFactor / 100), 0);

                                return (
                                  <div>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                      🔍 예열 온도: {mixedAirTemp.toFixed(1)}℃ → {phPreheatingTemp.toFixed(1)}℃
                                    </div>
                                    0.335 × {Math.round(calculatedSupplyAirflow).toLocaleString()} ㎥/h × ({phPreheatingTemp.toFixed(1)} - {mixedAirTemp.toFixed(1)}) ℃ × {((1 + phSafetyFactor / 100)).toString().replace(/\.?0+$/, '')} ＝
                                    <strong style={{ color: '#d32f2f', fontSize: '1.1rem' }}>
                                      {phLoad.toLocaleString()} W
                                    </strong>
                                  </div>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#ffe6f0', fontWeight: 'bold' }}>
                              가열
                            </TableCell>
                            <TableCell>
                              {heatingCalculationError ? (
                                <div>
                                  <div style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '8px' }}>
                                    ❌ 계산 실패
                                  </div>
                                  <div style={{ color: '#666', fontSize: '0.9rem', backgroundColor: '#ffebee', padding: '8px', borderRadius: '4px' }}>
                                    원인: {heatingCalculationError}
                                  </div>
                                </div>
                              ) : (
                                (() => {
                                  // 화면 표시용 값들 (반올림)
                                  const displaySupplyAirflow = Math.round(calculatedSupplyAirflow * 10) / 10; // 소수점 1자리
                                  const displaySupplyTemp = Math.round(supplyAirTemp * 10) / 10;
                                  const displayMixedTemp = Math.round(mixedAirTemp * 10) / 10;
                                  const displayTempDiff = Math.round((displaySupplyTemp - displayMixedTemp) * 10) / 10; // 온도차도 반올림
                                  const displaySafetyFactor = Math.round((1 + calculatedHeatingSafetyFactor / 100) * 100) / 100; // 소수점 2자리

                                  // 화면 표시값으로 계산한 결과 (수식과 일치)
                                  // VBA와 동일하게 올림 처리 (Application.RoundUp)
                                  const displayHeatingLoad = PsychrometricCalculator.vbaRoundUp(0.335 * displaySupplyAirflow * displayTempDiff * displaySafetyFactor, 0);

                                  return (
                                    <div>
                                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                                        🔍 사용된 온도: ma={displayMixedTemp.toFixed(1)}℃, l2={displaySupplyTemp.toFixed(1)}℃
                                      </div>
                                      0.335 × {displaySupplyAirflow.toLocaleString('en-US', {minimumFractionDigits: 1, maximumFractionDigits: 1})} ㎥/h × ({displaySupplyTemp.toFixed(1)} - {displayMixedTemp.toFixed(1)}) ℃ × {displaySafetyFactor.toFixed(1).replace(/\.0$/, '')} ＝
                                      <strong style={{ color: '#d32f2f', fontSize: '1.1rem' }}>
                                        {displayHeatingLoad.toLocaleString()} W
                                      </strong>
                                    </div>
                                  );
                                })()
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                              가습
                            </TableCell>
                            <TableCell>
                              {formData.humidifierType && formData.humidifierType !== 'None' ? (
                                <>
                                  1.2 × {Math.round(outdoorAirflow).toLocaleString()} ㎥/h × ({indoorHumidityRatio.toFixed(4)} - {outdoorHumidityRatio.toFixed(4)}) kg/kg' × {((1 + humidifierSafetyFactor / 100)).toString().replace(/\.?0+$/, '')} ＝
                                  <strong style={{ color: '#f57c00', fontSize: '1.1rem' }}>
                                    {Math.round(calculatedHumidifierLoad).toLocaleString()} kg/h
                                  </strong>
                                </>
                              ) : (
                                ''
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* 코일 형식 / 가습기 제원 */}
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                      □ 코일 형식 / 가습기 제원
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>Cool'g Coil</TableCell>
                            <TableCell>Water (7/12 ℃, 661 lpm)</TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>P/H Coil</TableCell>
                            <TableCell>None</TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>가습기 형식</TableCell>
                            <TableCell>{formData.humidifierType || 'None'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>R/H Coil</TableCell>
                            <TableCell>None</TableCell>
                            <TableCell sx={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>A/H Coil</TableCell>
                            <TableCell>Water (80/70 ℃, 188 lpm)</TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}

              {/* PSYCHROMETRIC CHART / SYSTEM DIAGRAM */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" component="div" sx={{
                  backgroundColor: '#000',
                  color: 'white',
                  p: 1,
                  fontWeight: 'bold'
                }}>
                  ■ PSYCHROMETRIC CHART
                </Typography>

                {/* 습공기 선도 전체 폭 사용 */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      □ 습공기 선도 (Psychrometric Chart)
                    </Typography>

                    {/* 습공기 선도 - 상태점 정보(좌) + 차트 이미지(우) */}
                    <Box sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: '#fafafa'
                    }}>
                      {(() => {
                        // 저장된 고해상도 이미지와 points 데이터 확인
                        const savedImage = ahu?.psychroChartImage;  // psychroChartImage 필드 사용
                        const points = ahu?.psychroChartData?.points || [];

                        // 디버깅 로그
                        // console.log(`[${ahu.ahuNo}] psychroChartData 확인:`, {
                        //   hasPsychroChartData: !!ahu?.psychroChartData,
                        //   hasPsychroChartImage: !!ahu?.psychroChartImage,
                        //   hasImage: !!savedImage,
                        //   hasPoints: points.length > 0,
                        //   imageLength: savedImage?.length || 0
                        // });

                        if (!savedImage) {
                          return (
                            <Box sx={{ p: 2, textAlign: 'center', color: 'warning.main' }}>
                              습공기 선도 이미지가 없습니다 ({ahu.ahuNo}). '풍량 선정' 탭에서 급기량을 계산하면 자동으로 생성됩니다.
                            </Box>
                          );
                        }

                        // Grid 레이아웃: 좌측 25% 상태점 정보, 우측 75% 차트 이미지
                        return (
                          <Grid container spacing={1}>
                            {/* 좌측: 상태점 정보 (25%) */}
                            <Grid item xs={3}>
                              <StatePointsInfo points={points} />
                            </Grid>

                            {/* 우측: 습공기 선도 차트 이미지 (75%) */}
                            <Grid item xs={9}>
                              <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%'
                              }}>
                                <img
                                  src={savedImage}
                                  alt="습공기 선도 (Psychrometric Chart)"
                                  style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })()}
                    </Box>
                </Box>

              </Box>

              {/* FAN SELECTION & ENTERING/LEAVING AIR STATUS - VBA Original Layout */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {/* Right Column: FAN SELECTION & ENTERING/LEAVING */}
                  <Grid item xs={12}>
                    {(() => {
                      const fanData = ahu.fanSelectionData;
                      const supplyFan = fanData?.supplyFan || {};
                      const returnFan = fanData?.returnFan || {};
                      const calc = ahu.calculationResults || {};
                      //console.log('🔍 [출력시트] AHU ID:', ahu.id, 'AHU번호:', ahu.ahuNo, '| sfLoad%:', calc.sfLoadPercentage, '| rfLoad%:', calc.rfLoadPercentage);
                      const cellStyle = { border: '1px solid #000', p: 0.3, fontSize: '0.75rem' };
                      const headerStyle = { ...cellStyle, backgroundColor: '#d9d9d9', fontWeight: 'bold' };
                      // FAN SELECTION 라벨 스타일 (라벨:값 = 2:1 비율)
                      const fanLabelStyle = { ...cellStyle, backgroundColor: '#f0f0f0', width: '200px' };
                      const fanValueStyle = { ...cellStyle, width: '100px' };
                      // ENTERING/LEAVING AIR STATUS 라벨 및 값/단위 셀 스타일
                      const labelStyle = { ...cellStyle, backgroundColor: '#f0f0f0', width: '150px' };
                      const valueStyle = { ...cellStyle, width: '60px' };
                      const unitStyle = { ...cellStyle, width: '55px' };

                      return (
                        <Box>
                          {/* FAN SELECTION Section */}
                          <Typography variant="subtitle1" component="div" sx={{
                            backgroundColor: '#000',
                            color: 'white',
                            p: 0.5,
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            mb: 1
                          }}>
                            ■ FAN SELECTION
                          </Typography>

                          <Grid container spacing={1}>
                            {/* Internal S.P Section */}
                            <Grid item xs={4}>
                              <Table size="small" sx={{ border: '1px solid #000' }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={headerStyle}>● Internal S.P (Pa)</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>냉각 코일</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.coolingCoil || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>재열 코일</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.reheatingCoil || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>예열 코일</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.preheatingCoil || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>가열 코일</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.heatingCoil || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>배열회수기</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.heatRecovery || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Pre Filter</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.preFilter || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Medium Filter</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.mediumFilter || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Final Filter</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.finalFilter || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Plenum/Casing</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.plenumCasing || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Chamber</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.chamber || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Volume Damper</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.internalPressureDrop?.volumeDamper || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ ...fanLabelStyle, borderTop: '2px solid #000', fontWeight: 'bold' }}>Safety Factor ({fanData?.internalPressureDrop?.safetyFactorPercent || 10}%)</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, borderTop: '2px solid #000', fontWeight: 'bold' }} align="right">{(fanData?.internalPressureDrop?.safetyFactorValue || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ ...fanLabelStyle, fontWeight: 'bold' }}>Sub-Total</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, fontWeight: 'bold' }} align="right">{(fanData?.internalPressureDrop?.subTotal || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Grid>

                            {/* External S.P Section */}
                            <Grid item xs={4}>
                              <Table size="small" sx={{ border: '1px solid #000' }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={4} sx={headerStyle}>● External S.P (Pa)</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Duct</TableCell>
                                    <TableCell sx={fanValueStyle} align="center">(SA)</TableCell>
                                    <TableCell sx={fanValueStyle} align="center">(RA)</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>  S:{fanData?.externalPressureDrop?.supply?.ductFriction1 || 1.5} Pa/m × {fanData?.externalPressureDrop?.supply?.ductLength1 || 0}m</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">
                                      {(() => {
                                        const friction = fanData?.externalPressureDrop?.supply?.ductFriction1 || 0;
                                        const length = fanData?.externalPressureDrop?.supply?.ductLength1 || 0;
                                        const value = Math.round(friction * length * 10) / 10;
                                        return (friction * length !== 0) ? value.toFixed(1) : '';
                                      })()}
                                    </TableCell>
                                    <TableCell sx={fanValueStyle} align="right"></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>  S:{fanData?.externalPressureDrop?.supply?.ductFriction2 || 1.0} Pa/m × {fanData?.externalPressureDrop?.supply?.ductLength2 || 0}m</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">
                                      {(() => {
                                        const friction = fanData?.externalPressureDrop?.supply?.ductFriction2 || 0;
                                        const length = fanData?.externalPressureDrop?.supply?.ductLength2 || 0;
                                        const value = Math.round(friction * length * 10) / 10;
                                        return (friction * length !== 0) ? value.toFixed(1) : '';
                                      })()}
                                    </TableCell>
                                    <TableCell sx={fanValueStyle} align="right"></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>  R:{fanData?.externalPressureDrop?.return?.ductFriction1 || 1.0} Pa/m × {fanData?.externalPressureDrop?.return?.ductLength1 || 0}m</TableCell>
                                    <TableCell sx={fanValueStyle} align="right"></TableCell>
                                    <TableCell sx={fanValueStyle} align="right">
                                      {(() => {
                                        const friction = fanData?.externalPressureDrop?.return?.ductFriction1 || 0;
                                        const length = fanData?.externalPressureDrop?.return?.ductLength1 || 0;
                                        const value = Math.round(friction * length * 10) / 10;
                                        return (friction * length !== 0) ? value.toFixed(1) : '';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>  R:{fanData?.externalPressureDrop?.return?.ductFriction2 || 0.8} Pa/m × {fanData?.externalPressureDrop?.return?.ductLength2 || 0}m</TableCell>
                                    <TableCell sx={fanValueStyle} align="right"></TableCell>
                                    <TableCell sx={fanValueStyle} align="right">
                                      {(() => {
                                        const friction = fanData?.externalPressureDrop?.return?.ductFriction2 || 0;
                                        const length = fanData?.externalPressureDrop?.return?.ductLength2 || 0;
                                        const value = Math.round(friction * length * 10) / 10;
                                        return (friction * length !== 0) ? value.toFixed(1) : '';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Fittings (50% of Duct)</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.ductFittings?.saValue && fanData.externalPressureDrop.ductFittings.saValue !== 0) ? fanData.externalPressureDrop.ductFittings.saValue.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.ductFittings?.raValue && fanData.externalPressureDrop.ductFittings.raValue !== 0) ? fanData.externalPressureDrop.ductFittings.raValue.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Terminal Unit</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.terminalUnit?.sa && fanData.externalPressureDrop.equipment.terminalUnit.sa !== 0) ? fanData.externalPressureDrop.equipment.terminalUnit.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.terminalUnit?.ra && fanData.externalPressureDrop.equipment.terminalUnit.ra !== 0) ? fanData.externalPressureDrop.equipment.terminalUnit.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Pre(Re) Heating Coil</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.preReHeatCoil?.sa && fanData.externalPressureDrop.equipment.preReHeatCoil.sa !== 0) ? fanData.externalPressureDrop.equipment.preReHeatCoil.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.preReHeatCoil?.ra && fanData.externalPressureDrop.equipment.preReHeatCoil.ra !== 0) ? fanData.externalPressureDrop.equipment.preReHeatCoil.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>OA/EA Louver</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.oaEaLouver?.sa && fanData.externalPressureDrop.equipment.oaEaLouver.sa !== 0) ? fanData.externalPressureDrop.equipment.oaEaLouver.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.oaEaLouver?.ra && fanData.externalPressureDrop.equipment.oaEaLouver.ra !== 0) ? fanData.externalPressureDrop.equipment.oaEaLouver.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Diffuser/Grille</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.diffuserGrille?.sa && fanData.externalPressureDrop.equipment.diffuserGrille.sa !== 0) ? fanData.externalPressureDrop.equipment.diffuserGrille.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.diffuserGrille?.ra && fanData.externalPressureDrop.equipment.diffuserGrille.ra !== 0) ? fanData.externalPressureDrop.equipment.diffuserGrille.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Flexible Duct</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.flexibleDuct?.sa && fanData.externalPressureDrop.equipment.flexibleDuct.sa !== 0) ? fanData.externalPressureDrop.equipment.flexibleDuct.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.flexibleDuct?.ra && fanData.externalPressureDrop.equipment.flexibleDuct.ra !== 0) ? fanData.externalPressureDrop.equipment.flexibleDuct.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Volume Damper</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.volumeDamper?.sa && fanData.externalPressureDrop.equipment.volumeDamper.sa !== 0) ? fanData.externalPressureDrop.equipment.volumeDamper.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.volumeDamper?.ra && fanData.externalPressureDrop.equipment.volumeDamper.ra !== 0) ? fanData.externalPressureDrop.equipment.volumeDamper.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Fire Damper</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.fireDamper?.sa && fanData.externalPressureDrop.equipment.fireDamper.sa !== 0) ? fanData.externalPressureDrop.equipment.fireDamper.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.fireDamper?.ra && fanData.externalPressureDrop.equipment.fireDamper.ra !== 0) ? fanData.externalPressureDrop.equipment.fireDamper.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Sound Attenuator</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.soundAttenuator?.sa && fanData.externalPressureDrop.equipment.soundAttenuator.sa !== 0) ? fanData.externalPressureDrop.equipment.soundAttenuator.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.soundAttenuator?.ra && fanData.externalPressureDrop.equipment.soundAttenuator.ra !== 0) ? fanData.externalPressureDrop.equipment.soundAttenuator.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>RA Plenum</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.raPlenum?.sa && fanData.externalPressureDrop.equipment.raPlenum.sa !== 0) ? fanData.externalPressureDrop.equipment.raPlenum.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.raPlenum?.ra && fanData.externalPressureDrop.equipment.raPlenum.ra !== 0) ? fanData.externalPressureDrop.equipment.raPlenum.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Velocity Pressure</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.velocityPressure?.sa && fanData.externalPressureDrop.equipment.velocityPressure.sa !== 0) ? fanData.externalPressureDrop.equipment.velocityPressure.sa.toFixed(1) : ''}</TableCell>
                                    <TableCell sx={fanValueStyle} align="right">{(fanData?.externalPressureDrop?.equipment?.velocityPressure?.ra && fanData.externalPressureDrop.equipment.velocityPressure.ra !== 0) ? fanData.externalPressureDrop.equipment.velocityPressure.ra.toFixed(1) : ''}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ ...fanLabelStyle, borderTop: '2px solid #000', fontWeight: 'bold' }}>Safety Factor ({fanData?.externalPressureDrop?.sa?.safetyFactorPercent || 10}%)</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, borderTop: '2px solid #000', fontWeight: 'bold' }} align="right">{(fanData?.externalPressureDrop?.sa?.safetyFactorValue || 0).toFixed(1)}</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, borderTop: '2px solid #000', fontWeight: 'bold' }} align="right">{(fanData?.externalPressureDrop?.ra?.safetyFactorValue || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ ...fanLabelStyle, fontWeight: 'bold' }}>Sub-Total</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, fontWeight: 'bold' }} align="right">{(fanData?.externalPressureDrop?.sa?.subTotal || 0).toFixed(1)}</TableCell>
                                    <TableCell sx={{ ...fanValueStyle, fontWeight: 'bold' }} align="right">{(fanData?.externalPressureDrop?.ra?.subTotal || 0).toFixed(1)}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Grid>

                            {/* Supply Fan, Return Fan, Power Calculation, Static Pressure */}
                            <Grid item xs={4}>
                              {/* Supply Fan */}
                              <Table size="small" sx={{ border: '1px solid #000', mb: 1 }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={headerStyle}>■ Supply Fan</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Type :</TableCell>
                                    <TableCell sx={fanValueStyle}>{supplyFan?.type || '-'}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Model :</TableCell>
                                    <TableCell sx={fanValueStyle}>{supplyFan?.specification || '-'}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Air Vol. :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const airflow = supplyFan?.airflow || 0;
                                        const ratio = fanData?.criteria?.saFanRatio || 1;
                                        const airflowPerUnit = Math.ceil(airflow / ratio);
                                        return airflow > 0 ? `${airflowPerUnit.toLocaleString()} ㎥/h x ${ratio}` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Static P :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const internalSubTotal = fanData?.internalPressureDrop?.subTotal || 0;
                                        const saSubTotal = fanData?.externalPressureDrop?.sa?.subTotal || 0;
                                        const spsTotal = Math.round((internalSubTotal + saSubTotal) * 10) / 10;
                                        return spsTotal > 0 ? `${Math.ceil(spsTotal)} Pa` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Motor :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const power = supplyFan?.power || 0;
                                        const ratio = fanData?.criteria?.saFanRatio || 1;
                                        return power > 0 ? `${power.toFixed(2)} kW x ${ratio}` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Location :</TableCell>
                                    <TableCell sx={fanValueStyle}>{fanData?.criteria?.saFanPosition || '-'}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>

                              {/* Return Fan */}
                              <Table size="small" sx={{ border: '1px solid #000', mb: 1 }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={headerStyle}>■ Return Fan</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Type :</TableCell>
                                    <TableCell sx={fanValueStyle}>{returnFan?.type || '-'}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Model :</TableCell>
                                    <TableCell sx={fanValueStyle}>{returnFan?.specification || '-'}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Air Vol. :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const airflow = returnFan?.airflow || 0;
                                        const ratio = fanData?.criteria?.raFanRatio || 1;
                                        const airflowPerUnit = Math.ceil(airflow / ratio);
                                        return airflow > 0 ? `${airflowPerUnit.toLocaleString()} ㎥/h x ${ratio}` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Static P :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const staticP = returnFan?.staticPressure || 0;
                                        return staticP > 0 ? `${Math.ceil(staticP)} Pa` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Motor :</TableCell>
                                    <TableCell sx={fanValueStyle}>
                                      {(() => {
                                        const power = returnFan?.power || 0;
                                        const ratio = fanData?.criteria?.raFanRatio || 1;
                                        return power > 0 ? `${power.toFixed(2)} kW x ${ratio}` : '-';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={fanLabelStyle}>Location :</TableCell>
                                    <TableCell sx={fanValueStyle}>{fanData?.criteria?.raFanPosition || '-'}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>

                              {/* Power Calculation */}
                              <Table size="small" sx={{ border: '1px solid #000', mb: 1 }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={headerStyle}>■ Power Calculation</TableCell>
                                  </TableRow>
                                  {(() => {
                                    // Supply Fan Power 계산 - SPs 값 사용
                                    const saAirflow = Math.round(supplyFan?.airflow || 0);
                                    const internalSubTotal = fanData?.internalPressureDrop?.subTotal || 0;
                                    const saSubTotal = fanData?.externalPressureDrop?.sa?.subTotal || 0;
                                    const spsTotal = Math.round((internalSubTotal + saSubTotal) * 10) / 10;
                                    const saStaticP = Math.round(spsTotal);
                                    const saEfficiency = (supplyFan?.efficiency || 65) / 100;
                                    const saSafetyFactor = (supplyFan?.safetyFactor || 15) / 100;
                                    const saPower = ((saAirflow * saStaticP) / (3600 * 1000 * saEfficiency) * (1 + saSafetyFactor));

                                    return (
                                      <TableRow>
                                        <TableCell colSpan={2} sx={cellStyle}>
                                          Ps = {saAirflow}㎥/h×{saStaticP}Pa÷(3600×1000×{saEfficiency})×{(1 + saSafetyFactor).toFixed(2)} = {saPower.toFixed(2)} kW
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}
                                  {(() => {
                                    // Return Fan Power 계산
                                    const raAirflow = Math.round(returnFan?.airflow || 0);
                                    const raStaticP = Math.round(returnFan?.staticPressure || 0);
                                    const raEfficiency = (returnFan?.efficiency || 50) / 100;
                                    const raSafetyFactor = (returnFan?.safetyFactor || 15) / 100;
                                    const raPower = ((raAirflow * raStaticP) / (3600 * 1000 * raEfficiency) * (1 + raSafetyFactor));

                                    // Return Fan이 None이 아닐 때만 표시
                                    if (fanData?.criteria?.raFanPosition && fanData.criteria.raFanPosition !== 'None') {
                                      return (
                                        <TableRow>
                                          <TableCell colSpan={2} sx={cellStyle}>
                                            Pr = {raAirflow}㎥/h×{raStaticP}Pa÷(3600×1000×{raEfficiency})×{(1 + raSafetyFactor).toFixed(2)} = {raPower.toFixed(2)} kW
                                          </TableCell>
                                        </TableRow>
                                      );
                                    }
                                    return null;
                                  })()}
                                </TableBody>
                              </Table>

                              {/* Static Pressure */}
                              <Table size="small" sx={{ border: '1px solid #000' }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={3} sx={headerStyle}>■ Static Pressure</TableCell>
                                  </TableRow>
                                  {(() => {
                                    // FAN 선정 탭에서 계산된 Sub-Total 값 참조
                                    const internalSubTotal = fanData?.internalPressureDrop?.subTotal || 0;
                                    const saSubTotal = fanData?.externalPressureDrop?.sa?.subTotal || 0;
                                    const spsTotal = Math.round((internalSubTotal + saSubTotal) * 10) / 10;

                                    return (
                                      <>
                                        <TableRow>
                                          <TableCell colSpan={3} sx={cellStyle}>
                                            SPs = {internalSubTotal} + {saSubTotal} = {spsTotal} Pa
                                          </TableCell>
                                        </TableRow>
                                      </>
                                    );
                                  })()}
                                </TableBody>
                              </Table>
                            </Grid>
                          </Grid>

                          {/* ENTERING/LEAVING AIR STATUS Section */}
                          <Typography variant="subtitle1" component="div" sx={{
                            backgroundColor: '#000',
                            color: 'white',
                            p: 0.5,
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            mt: 2,
                            mb: 1
                          }}>
                            ■ ENTERING/LEAVING AIR STATUS
                          </Typography>

                          <Table size="small" sx={{ border: '1px solid #000' }}>
                            <TableBody>
                              {/* OA Conditions Header */}
                              <TableRow>
                                <TableCell sx={labelStyle} rowSpan={2}>OA' Cond. -</TableCell>
                                <TableCell sx={cellStyle}>Cooling</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.outdoorAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.outdoorAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.outdoorAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.outdoorAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={cellStyle}>Heating</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.heatingOutdoorAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.heatingOutdoorAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.heatingOutdoorAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.heatingOutdoorAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                              </TableRow>

                              {/* Cooling Coil Part */}
                              <TableRow>
                                <TableCell colSpan={10} sx={headerStyle}>● Cooling Coil Part</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}>SA / RA Fan Load</TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    const supplyFan = fanData?.supplyFan;
                                    // 계산에 사용된 inputs.sfLoadPercentage 값을 calc에서 직접 가져옴
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const airflow = supplyFan?.airflow || 0;
                                    const staticPressure = supplyFan?.staticPressure || 0;
                                    const efficiency = (supplyFan?.efficiency || 65) / 100;
                                    const safetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      return `SF Load(Q2) = (${airflow.toLocaleString()} × ${staticPressure} / (3600 × ${efficiency.toFixed(2)}) × ${safetyFactor.toFixed(2)}) x ${saFanRatio.toFixed(2)} =`;
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      return `SF Load(Q2) = (${airflow.toLocaleString()} × ${staticPressure} / (3600 × ${efficiency.toFixed(2)})) x ${saFanRatio.toFixed(2)} =`;
                                    }
                                    return 'SF Load(Q2) =';
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {(() => {
                                    const supplyFan = fanData?.supplyFan;
                                    // 계산에 사용된 inputs.sfLoadPercentage 값을 calc에서 직접 가져옴
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const airflow = supplyFan?.airflow || 0;
                                    const staticPressure = supplyFan?.staticPressure || 0;
                                    const efficiency = (supplyFan?.efficiency || 65) / 100;
                                    const safetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    let sfLoad = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      // Inside: 안전율 포함
                                      sfLoad = Math.round((airflow * staticPressure / (3600 * efficiency) * safetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      // Outside: 안전율 미포함
                                      sfLoad = Math.round((airflow * staticPressure / (3600 * efficiency)) * saFanRatio);
                                    }
                                    return sfLoad > 0 ? sfLoad.toLocaleString() : '-';
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">W</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';

                                    if (raFanPosition === 'None') {
                                      return 'RF Load (Q3) =';
                                    }

                                    const airflow = returnFan?.airflow || 0;
                                    const staticPressure = returnFan?.staticPressure || 0;
                                    const efficiency = (returnFan?.efficiency || 50) / 100;
                                    const safetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    if (raFanPosition === 'Inside') {
                                      return `RF Load(Q3) = (${airflow.toLocaleString()} × ${staticPressure} / (3600 × ${efficiency.toFixed(2)}) × ${safetyFactor.toFixed(2)}) x ${raFanRatio.toFixed(2)} =`;
                                    } else if (raFanPosition === 'Outside') {
                                      return `RF Load(Q3) = (${airflow.toLocaleString()} × ${staticPressure} / (3600 × ${efficiency.toFixed(2)})) x ${raFanRatio.toFixed(2)} =`;
                                    }
                                    return 'RF Load (Q3) =';
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {(() => {
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';

                                    if (raFanPosition === 'None') {
                                      return '-';
                                    }

                                    const airflow = returnFan?.airflow || 0;
                                    const staticPressure = returnFan?.staticPressure || 0;
                                    const efficiency = (returnFan?.efficiency || 50) / 100;
                                    const safetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    let rfLoad = 0;
                                    if (raFanPosition === 'Inside') {
                                      rfLoad = Math.round((airflow * staticPressure / (3600 * efficiency) * safetyFactor) * raFanRatio);
                                    } else if (raFanPosition === 'Outside') {
                                      rfLoad = Math.round((airflow * staticPressure / (3600 * efficiency)) * raFanRatio);
                                    }
                                    return rfLoad > 0 ? rfLoad.toLocaleString() : '0';
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">W</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}>RA' Condition</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.returnAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.returnAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.returnAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.returnAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    // Q1 = 천장속(플레넘) 조명에 의한 RA 온도상승 부하 (Cceil)
                                    // '풍량 선정' 탭의 "환기 조명부하(W)" 입력값이 calc.ceilingLightingLoad로 저장됨
                                    const q1 = calc.ceilingLightingLoad || 0;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    // Q2 계산 (SF Load)
                                    const supplyFan = fanData?.supplyFan;
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const airflow = supplyFan?.airflow || 0;
                                    const staticPressure = supplyFan?.staticPressure || 0;
                                    const efficiency = (supplyFan?.efficiency || 65) / 100;
                                    const safetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;

                                    let q2 = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      q2 = Math.round((airflow * staticPressure / (3600 * efficiency) * safetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      q2 = Math.round((airflow * staticPressure / (3600 * efficiency)) * saFanRatio);
                                    }

                                    // Q3 계산 (RF Load)
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';
                                    const raAirflow = returnFan?.airflow || 0;
                                    const raStaticPressure = returnFan?.staticPressure || 0;
                                    const raEfficiency = (returnFan?.efficiency || 50) / 100;
                                    const raSafetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    let q3 = 0;
                                    if (raFanPosition !== 'None') {
                                      if (raFanPosition === 'Inside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency) * raSafetyFactor) * raFanRatio);
                                      } else if (raFanPosition === 'Outside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency)) * raFanRatio);
                                      }
                                    }

                                    if (saFanPosition.startsWith('Blow')) {
                                      return `RA Load = Q1 + Q2(Blow Type) + Q3 = ${Math.round(q1)} + ${q2} + ${q3} =`;
                                    } else {
                                      return `RA Load = Q1 + Q3 = ${Math.round(q1)} + ${q3} =`;
                                    }
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {(() => {
                                    // Q1 = 천장속(플레넘) 조명에 의한 RA 온도상승 부하 (Cceil)
                                    // '풍량 선정' 탭의 "환기 조명부하(W)" 입력값이 calc.ceilingLightingLoad로 저장됨
                                    const q1 = calc.ceilingLightingLoad || 0;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    // Q2 계산 (SF Load)
                                    const supplyFan = fanData?.supplyFan;
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const airflow = supplyFan?.airflow || 0;
                                    const staticPressure = supplyFan?.staticPressure || 0;
                                    const efficiency = (supplyFan?.efficiency || 65) / 100;
                                    const safetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;

                                    let q2 = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      q2 = Math.round((airflow * staticPressure / (3600 * efficiency) * safetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      q2 = Math.round((airflow * staticPressure / (3600 * efficiency)) * saFanRatio);
                                    }

                                    // Q3 계산 (RF Load)
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';
                                    const raAirflow = returnFan?.airflow || 0;
                                    const raStaticPressure = returnFan?.staticPressure || 0;
                                    const raEfficiency = (returnFan?.efficiency || 50) / 100;
                                    const raSafetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    let q3 = 0;
                                    if (raFanPosition !== 'None') {
                                      if (raFanPosition === 'Inside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency) * raSafetyFactor) * raFanRatio);
                                      } else if (raFanPosition === 'Outside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency)) * raFanRatio);
                                      }
                                    }

                                    const raLoad = saFanPosition.startsWith('Blow') ? Math.round(q1) + q2 + q3 : Math.round(q1) + q3;
                                    return raLoad.toLocaleString();
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">W</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    // RA Load 재계산 (위의 Q1 + Q2 + Q3 로직과 동일)
                                    // Q1 = 천장속(플레넘) 조명에 의한 RA 온도상승 부하 (Cceil)
                                    // '풍량 선정' 탭의 "환기 조명부하(W)" 입력값이 calc.ceilingLightingLoad로 저장됨
                                    const q1 = calc.ceilingLightingLoad || 0;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    // Q2 계산
                                    const supplyFan = fanData?.supplyFan;
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const sfAirflow = supplyFan?.airflow || 0;
                                    const sfStaticPressure = supplyFan?.staticPressure || 0;
                                    const sfEfficiency = (supplyFan?.efficiency || 65) / 100;
                                    const sfSafetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;

                                    let q2 = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      q2 = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency) * sfSafetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      q2 = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency)) * saFanRatio);
                                    }

                                    // Q3 계산
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';
                                    const raAirflow = returnFan?.airflow || 0;
                                    const raStaticPressure = returnFan?.staticPressure || 0;
                                    const raEfficiency = (returnFan?.efficiency || 50) / 100;
                                    const raSafetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    let q3 = 0;
                                    if (raFanPosition !== 'None') {
                                      if (raFanPosition === 'Inside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency) * raSafetyFactor) * raFanRatio);
                                      } else if (raFanPosition === 'Outside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency)) * raFanRatio);
                                      }
                                    }

                                    const raLoad = saFanPosition.startsWith('Blow') ? Math.round(q1) + q2 + q3 : Math.round(q1) + q3;
                                    const raTemp = raAirflow > 0 ? raLoad / (0.335 * raAirflow) : 0;

                                    return `RA Temp. = ${raLoad.toLocaleString()} W ÷ (0.335 × ${raAirflow.toLocaleString()} ㎥/h) = ${raTemp.toFixed(2)} →`;
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {(() => {
                                    // RA Temp 계산
                                    // Q1 = 천장속(플레넘) 조명에 의한 RA 온도상승 부하 (Cceil)
                                    // '풍량 선정' 탭의 "환기 조명부하(W)" 입력값이 calc.ceilingLightingLoad로 저장됨
                                    const q1 = calc.ceilingLightingLoad || 0;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    // Q2 계산
                                    const supplyFan = fanData?.supplyFan;
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const sfAirflow = supplyFan?.airflow || 0;
                                    const sfStaticPressure = supplyFan?.staticPressure || 0;
                                    const sfEfficiency = (supplyFan?.efficiency || 65) / 100;
                                    const sfSafetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;

                                    let q2 = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      q2 = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency) * sfSafetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      q2 = Math.round((sfAirflow * sfStaticPressure / (3600 * sfEfficiency)) * saFanRatio);
                                    }

                                    // Q3 계산
                                    const returnFan = fanData?.returnFan;
                                    const rfLoadPercentage = calc?.rfLoadPercentage;
                                    const raFanRatio = rfLoadPercentage / 100;
                                    const raFanPosition = fanData?.criteria?.raFanPosition || 'Inside';
                                    const raAirflow = returnFan?.airflow || 0;
                                    const raStaticPressure = returnFan?.staticPressure || 0;
                                    const raEfficiency = (returnFan?.efficiency || 50) / 100;
                                    const raSafetyFactor = 1 + (returnFan?.safetyFactor || 15) / 100;

                                    let q3 = 0;
                                    if (raFanPosition !== 'None') {
                                      if (raFanPosition === 'Inside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency) * raSafetyFactor) * raFanRatio);
                                      } else if (raFanPosition === 'Outside') {
                                        q3 = Math.round((raAirflow * raStaticPressure / (3600 * raEfficiency)) * raFanRatio);
                                      }
                                    }

                                    const raLoad = saFanPosition.startsWith('Blow') ? Math.round(q1) + q2 + q3 : Math.round(q1) + q3;
                                    const raTemp = raAirflow > 0 ? raLoad / (0.335 * raAirflow) : 0;

                                    return raTemp.toFixed(1);
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">℃</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}>Mixing Air Condition</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.mixedAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.mixedAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.mixedAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                <TableCell sx={valueStyle} align="right">{calc.mixedAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    const oaAirflow = calc.outdoorAirflow || 0;
                                    const supplyAirflow = supplyFan?.airflow || 0;
                                    const oaRatio = supplyAirflow > 0 ? oaAirflow / supplyAirflow : 0;
                                    const raRatio = 1 - oaRatio;
                                    const oaTemp = calc.outdoorAir?.dryBulbTemp || 0;
                                    const raTemp = calc.returnAir?.dryBulbTemp || 0;
                                    return `MAtemp = ${oaRatio.toFixed(2)} × ${oaTemp.toFixed(1)} ℃ + ${raRatio.toFixed(2)} × ${raTemp} ℃ = ${(oaRatio * oaTemp + raRatio * raTemp).toFixed(2)} →`;
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">{calc.mixedAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                <TableCell sx={cellStyle} align="center">℃ DB</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    const oaAirflow = calc.outdoorAirflow || 0;
                                    const supplyAirflow = supplyFan?.airflow || 0;
                                    const oaRatio = supplyAirflow > 0 ? oaAirflow / supplyAirflow : 0;
                                    const raRatio = 1 - oaRatio;
                                    const oaAbso = calc.outdoorAir?.humidityRatio || 0;
                                    const raAbso = calc.returnAir?.humidityRatio || 0;
                                    return `MAabso = ${oaRatio.toFixed(2)} × ${oaAbso.toFixed(4)} kg/kg' + ${raRatio.toFixed(2)} × ${raAbso.toFixed(4)} kg/kg' = ${(oaRatio.toFixed(2) * oaAbso.toFixed(4) + raRatio.toFixed(2) * raAbso.toFixed(4)).toFixed(5)} →`;
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">{calc.mixedAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                <TableCell sx={cellStyle} align="center">kg/kg'</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}>Leav'g Air Condition</TableCell>
                                {(() => {
                                  // l1(냉각후) 상태점과 동일한 값 사용
                                  // psychroChartData.ts의 l1 계산과 동일하게 처리
                                  const l1Point = ahu.psychroChartData?.points?.find((p: any) =>
                                    p.name === 'l1' || p.displayName?.includes('냉방코일출구')
                                  );

                                  // l1 상태점이 있으면 그 값을 사용, 없으면 calc.supplyAir 사용
                                  const dryBulbTemp = l1Point?.dryBulbTemp ?? calc.supplyAir?.dryBulbTemp;
                                  const relativeHumidity = l1Point?.relativeHumidity ?? calc.supplyAir?.relativeHumidity;
                                  const humidityRatio = l1Point?.humidityRatio ?? calc.supplyAir?.humidityRatio;
                                  const enthalpy = l1Point?.enthalpy ?? calc.supplyAir?.enthalpy;

                                  return (
                                    <>
                                      <TableCell sx={valueStyle} align="right">{dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                      <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                      <TableCell sx={valueStyle} align="right">{relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                      <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                      <TableCell sx={valueStyle} align="right">{humidityRatio?.toFixed(4) || '-'}</TableCell>
                                      <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                      <TableCell sx={valueStyle} align="right">{enthalpy?.toFixed(2) || '-'}</TableCell>
                                      <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                                    </>
                                  );
                                })()}
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    // SF Load (Q2) 계산
                                    const supplyFan = fanData?.supplyFan;
                                    const saFanRatio = (calc?.sfLoadPercentage) / 100;
                                    const airflow = supplyFan?.airflow || 0;
                                    const staticPressure = supplyFan?.staticPressure || 0;
                                    const efficiency = (supplyFan?.efficiency || 65) / 100;
                                    const safetyFactor = 1 + (supplyFan?.safetyFactor || 15) / 100;
                                    const saFanPosition = fanData?.criteria?.saFanPosition || 'Draw(I)';

                                    let sfLoad = 0;
                                    if (saFanPosition.startsWith('Blow(I)') || saFanPosition.startsWith('Draw(I)')) {
                                      sfLoad = Math.round((airflow * staticPressure / (3600 * efficiency) * safetyFactor) * saFanRatio);
                                    } else if (saFanPosition.startsWith('Blow(O)') || saFanPosition.startsWith('Draw(O)')) {
                                      sfLoad = Math.round((airflow * staticPressure / (3600 * efficiency)) * saFanRatio);
                                    }

                                    const supplyAirflow = airflow || 0;
                                    const saTemp = sfLoad / (0.335 * supplyAirflow);
                                    return `SA Temp. = ${sfLoad.toLocaleString()} W ÷ (0.335 × ${supplyAirflow.toLocaleString()} ㎥/h) = ${saTemp.toFixed(2)} →`;
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {calc.supplyFanTempRise?.toFixed(1) || '-'}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">℃</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={labelStyle}></TableCell>
                                <TableCell sx={cellStyle} colSpan={6}>
                                  {(() => {
                                    const rhReheatTemp = parseFloat(ahu.formData?.rhReheatTemp) || 0;
                                    if (rhReheatTemp === 0) {
                                      return 'R/H Temp. = (재열 없음)';
                                    }
                                    return `R/H Temp. = ${rhReheatTemp.toFixed(1)} →`;
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="right">
                                  {(() => {
                                    const rhReheatTemp = parseFloat(ahu.formData?.rhReheatTemp) || 0;
                                    return rhReheatTemp > 0 ? rhReheatTemp.toFixed(1) : '-';
                                  })()}
                                </TableCell>
                                <TableCell sx={cellStyle} align="center">℃</TableCell>
                              </TableRow>

                              {/* Heating Coil Part */}
                              {calc.heatingMixedAir && (
                                <>
                                  <TableRow>
                                    <TableCell colSpan={10} sx={headerStyle}>● Heating Coil Part</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={labelStyle}>Mixing Air Condition</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingMixedAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingMixedAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingMixedAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={labelStyle}></TableCell>
                                    <TableCell sx={cellStyle} colSpan={6}>
                                      {(() => {
                                        const oaAirflow = calc.outdoorAirflow || 0;
                                        const supplyAirflow = supplyFan?.airflow || 0;
                                        const oaRatio = supplyAirflow > 0 ? oaAirflow / supplyAirflow : 0;
                                        const raRatio = 1 - oaRatio;
                                        const oaTemp = calc.heatingOutdoorAir?.dryBulbTemp || 0;
                                        const raTemp = calc.heatingReturnAir?.dryBulbTemp || 0;
                                        return `MAtemp = ${oaRatio.toFixed(2)} × ${oaTemp.toFixed(1)} ℃ + ${raRatio.toFixed(2)} × ${raTemp.toFixed(1)} ℃ = ${(oaRatio.toFixed(2) * oaTemp.toFixed(1) + raRatio.toFixed(2) * raTemp.toFixed(1)).toFixed(2)} →`;
                                      })()}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">{calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={cellStyle} align="center">℃ DB</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={labelStyle}></TableCell>
                                    <TableCell sx={cellStyle} colSpan={6}>
                                      {(() => {
                                        const oaAirflow = calc.outdoorAirflow || 0;
                                        const supplyAirflow = supplyFan?.airflow || 0;
                                        const oaRatio = supplyAirflow > 0 ? oaAirflow / supplyAirflow : 0;
                                        const raRatio = 1 - oaRatio;
                                        const oaAbso = calc.heatingOutdoorAir?.humidityRatio || 0;
                                        const raAbso = calc.heatingReturnAir?.humidityRatio || 0;
                                        return `MAabso = ${oaRatio.toFixed(2)} × ${oaAbso.toFixed(4)} kg/kg' + ${raRatio.toFixed(2)} × ${raAbso.toFixed(4)} kg/kg' = ${(oaRatio.toFixed(2) * oaAbso.toFixed(4) + raRatio.toFixed(2) * raAbso.toFixed(4)).toFixed(5)} →`;
                                      })()}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">{calc.heatingMixedAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                    <TableCell sx={cellStyle} align="center">kg/kg'</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={labelStyle}>P/H Coil Air Cond.</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingSupplyAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">℃ DB</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingSupplyAir?.relativeHumidity?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">% RH</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingSupplyAir?.humidityRatio?.toFixed(4) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">kg/kg'</TableCell>
                                    <TableCell sx={valueStyle} align="right">{calc.heatingSupplyAir?.enthalpy?.toFixed(2) || '-'}</TableCell>
                                    <TableCell sx={unitStyle} align="center">kJ/kg</TableCell>
                                  </TableRow>
                                  {calc.heatingAfterHeating && (
                                    <TableRow>
                                      <TableCell sx={labelStyle}>A/H Coil Air Cond.</TableCell>
                                      <TableCell sx={cellStyle}>E.A.T :</TableCell>
                                      <TableCell sx={cellStyle}>{calc.heatingMixedAir?.dryBulbTemp?.toFixed(1) || '-'} ℃ DB</TableCell>
                                      <TableCell sx={cellStyle}>L.A.T :</TableCell>
                                      <TableCell sx={cellStyle}>{calc.heatingAfterHeating?.dryBulbTemp?.toFixed(1) || '-'} ℃ DB</TableCell>
                                      <TableCell sx={cellStyle} colSpan={5}></TableCell>
                                    </TableRow>
                                  )}
                                  <TableRow>
                                    <TableCell sx={labelStyle}></TableCell>
                                    <TableCell sx={cellStyle} colSpan={6}>
                                      {(() => {
                                        const heatingLoad = parseFloat(ahu.formData?.heatingLoad) || calc.heatingLoad || 0;
                                        const supplyAirflow = supplyFan?.airflow || 0;
                                        const raTemp = calc.heatingReturnAir?.dryBulbTemp || 0;
                                        return `LA Temp. = ${heatingLoad.toLocaleString()} W ÷ (0.335 × ${supplyAirflow.toLocaleString()} ㎥/h) + ${raTemp?.toFixed(1) || 0} ℃ =`;
                                      })()}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">{calc.heatingSupplyAir?.dryBulbTemp?.toFixed(1) || '-'}</TableCell>
                                    <TableCell sx={cellStyle} align="center">℃ DB</TableCell>
                                  </TableRow>
                                </>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              </Box>

              {index < ahuData.length - 1 && (
                <Box sx={{ borderBottom: '2px solid #ddd', mb: 4, pb: 2 }} />
              )}
            </Box>
          ))}
        </Box>
    </Paper>
  )
}

export default React.memo(AHSelInlinePreview)